import React, { useState, useEffect } from "react";
import { ChevronRight, FileText, Share2, Plus, Image as ImageIcon, X, Trash2, Send, Check, Sparkles, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface Leaflet {
  id: string;
  title: string;
  enTitle: string;
  category: string;
  summary: string;
  fullInformation: {
    whatIsIt: string;
    causes: string;
    symptoms: string;
    treatment: string;
    selfCare: string;
  };
  images: string[];
}

const DEFAULT_LEAFLETS: Leaflet[] = [
  {
    id: "eczema",
    title: "الإكزيما التأتبي (التهاب الجلد التحسسي)",
    enTitle: "Atopic Eczema (Dermatitis)",
    category: "الأمراض الالتهابية والحساسية",
    summary: "نشرة مفصلة من مرجع BAD تشرح طبيعة الإكزيما، كيفية العناية بالبشرة الجافة، والاستخدام الآمن للكورتيزون الموضعي والترطيب المستمر.",
    fullInformation: {
      whatIsIt: "الإكزيما التأتبي هو حالة جلدية شائعة تتسبب في أن يصبح الجلد جافاً، محمراً، ومتهيّجاً مع حكة شديدة. كلمة (تأتبي) تعني وجود استعداد وراثي للتحسس والربو.",
      causes: "تنتج الإكزيما عن مزيج من العامل الوراثي ونقص في وظيفة الجدار الواقي للجلد (Skin Barrier)، مما يجعله أكثر تأثراً بالمؤثرات الخارجية كالجفاف، الصابون، وتقلبات الطقس.",
      symptoms: "حكة شديدة ومزعجة، جفاف عام في البشرة، بقع حمراء ومتقشرة تتواجد غالباً في ثنيات الجلد (أمام الكوعين، خلف الركبتين، وحول العنق).",
      treatment: "العلاج الأساسي يعتمد على ركنين: 1. الترطيب المستمر والعميق (Emollients) لتعويض الجدار الجلدي. 2. الكريمات والمراهم الستيروئيدية الموضعية (Topical Steroids) للسيطرة على الالتهاب والتهيج تحت إشراف الطبيب.",
      selfCare: "تجنب استخدام الصابون التجاري المعطر واستبداله بغسول طبي لطيف (Soap Substitute). استحمام بماء فاتر تجنباً للماء الساخن الذي يزيد الجفاف. قص الأظافر لتفادي تجريح الجلد أثناء الحكة."
    },
    images: [
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "psoriasis",
    title: "الصدفية (Psoriasis)",
    enTitle: "Psoriasis",
    category: "الأمراض المناعية المزمنة",
    summary: "مرشد شامل لمرضى الصدفية يتناول أسباب تسارع تجدد خلايا الجلد، خيارات العلاج الموضعي والضوئي، ونصائح تحسين نمط الحياة.",
    fullInformation: {
      whatIsIt: "الصدفية مرض جلدي مناعي غير مُعدٍ، يؤدي إلى تسارع وتيرة إنتاج خلايا الجلد، مما يؤدي إلى تراكمها وتشكيل بقع سميكة محمرة مغطاة بقشور فضية بيضاء.",
      causes: "السبب الرئيسي هو نشاط زائد في جهاز المناعة يوجه إشارات خاطئة لخلايا الجلد للتقسيم السريع، إلى جانب عوامل محفزة مثل الضغط النفسي (الإجهاد)، بعض الالتهابات، أو برودة الجو.",
      symptoms: "بقع محمرة بارزة مغطاة بقشور فضية، تظهر غالباً في الكوعين، الركبتين، وفروة الرأس، وقد ترافقها تغيرات في الأظافر أو آلام في المفاصل لدى بعض المرضى.",
      treatment: "تشمل العلاجات الموضعية (كريمات فيتامين D والكورتيزون)، العلاج بالضوء (UVB)، وفي الحالات المتقدمة العلاجات البايلوجية والمثبتة للمناعة تحت متابعة طبيبة متخصصة.",
      selfCare: "الالتزام بالترطيب اليومي لتقليل القشور، التعرض المعتدل لآشعة الشمس الصباحية المفيدة، تجنب حك أو إزالة القشور بعنف (ظاهرة كوبر)، والحرص على الصحة النفسية."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "acne",
    title: "حب الشباب الشائع (Acne Vulgaris)",
    enTitle: "Acne Vulgaris",
    category: "أمراض الغدد الدهنية",
    summary: "دليل المريض العربي لعلاج حب الشباب من BAD: فهم أسباب ظهور البثور والمقاومة الدوائية والوقاية من التصبغات والندب.",
    fullInformation: {
      whatIsIt: "حب الشباب حالة جلدية تحدث عندما تنسد بصيلات الشعر بالزيوت (الزهم) والخلايا الجلدية الميتة، مما يسمح بنمو بكتيريا الجلد المسببة لالتهاب والبثور.",
      causes: "التغيرات الهرمونية العادية في فترة البلوغ أو عند النساء، فرط نشاط الغدد الدهنية، واستخدام مستحضرات تجميل انسدادية (Comedogenic).",
      symptoms: "رؤوس سوداء وبيضاء (زوان)، حبوب حمراء ملتهبة، وفي الحالات الأعمق أكياس أو عقيدات قد تترك آثاراً وتصبغات.",
      treatment: "الكريمات المقشرة (مشتقات فيتامين A مثل التريتينوين)، المضادات الحيوية الموضعية أو الفموية، وفي الحالات المعندة أو الكيسية دواء الإيزوتريتينوين (الروكتان) بإشراف طبي دقيق.",
      selfCare: "تجنب عصر أو تفجير الحبوب منعاً لترك ندبات وحفر. غسل الوجه مرتين يومياً بغسول مناسب لبشرتك دون إفراط في الفرك. استخدام واقي شمس خالي من الزيوت (Oil-free)."
    },
    images: [
      "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "alopecia",
    title: "الثعلبة البقعية (Alopecia Areata)",
    enTitle: "Alopecia Areata",
    category: "أمراض الشعر والمناعة الذاتية",
    summary: "معلومات شاملة وموثوقة عن تساقط الشعر البقعي المفاجئ وأفضل الطرق الموصى بها في المراجع البريطانية لاستعادة نمو الشعر.",
    fullInformation: {
      whatIsIt: "الثعلبة البقعية هي حالة تساقط شعر مفاجئ بشكل بقع دائرية خالية تماماً من الشعر في فروة الرأس أو اللحية أو الحواجب.",
      causes: "تُصنف كمرض مناعي ذاتي حيث يقوم جهاز المناعة بفرز خلايا بيضاء تهاجم بصيلات الشعر بالخطأ مؤقتةً، دون أن تتلف البصيلة بشكل دائم، مما يعني إمكانية معاودة النمو.",
      symptoms: "ظهور بقعة دائرية أو أكثر ناعمة الملمس تماماً وخالية من الشعر دون وجود قشور أو ألم شديد (قد يشعر البعض بوخز خفيف قبل ظهورها).",
      treatment: "الكريمات أو الحقن الموضعية بالكورتيزون لتحفيز نمو الشعر، بخاخ المينوكسيديل، والعلاجات المناعية الموضعية الحديثة (مثل معدلات المناعة).",
      selfCare: "الطمأنينة وتجنب التوتر النفسي الشديد لأنه قد يفاقم النوبات. حماية فروة الرأس من آشعة الشمس باستخدام قبعة أو واقي شمسي في حال اتساع المساحة."
    },
    images: [
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "vitiligo",
    title: "البهاق (Vitiligo)",
    enTitle: "Vitiligo",
    category: "أمراض التصبغات والجلد",
    summary: "نشرة توعية من BAD عن نقص خلايا الملاينين في الجلد، أهمية الحماية الضوئية، وأحدث بروتوكولات العلاج بالضوء والكريمات.",
    fullInformation: {
      whatIsIt: "البهاق هو مرض جلدي مسببه فقدان اللون الطبيعي للجلد على شكل بقع بيضاء حليبية، نتيجة توقف أو اختفاء خلايا الملاينين الصبغية المنتجة للون.",
      causes: "مرض ذو أساس مناعي ذاتي مع تدخل عوامل وراثية وبيئية كالإجهاد التأكسدي داخل الخلايا الصبغية.",
      symptoms: "بقع بيضاء محددة بشكل واضح يمكن أن تظهر في أي مكان في الجسم، خصوصاً حول الوجه، اليدين، والمفاصل.",
      treatment: "كريمات الكورتيزون الموضعية أو مثبطات الكالسينورين (تاكروليموس)، العلاج بالأشعة فوق البنفسجية (Narrowband UVB)، ومضادات الـ JAK المتقدمة.",
      selfCare: "استخدام واقي شمس عالي الحماية (SPF 50+) لأن البقع البيضاء تفقد حمايتها الطبيعية ومعرضة جداً للحروق، واستخدام مستحضرات الإخفاء التجميلية عند الرغبة."
    },
    images: [
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "melasma",
    title: "الكلف وتصبغات البشرة (Melasma)",
    enTitle: "Melasma & Hyperpigmentation",
    category: "أمراض التصبغات والجلد",
    summary: "دليل مرجع BAD لفهم أسباب ظهور البقع الداكنة على الوجنتين والجبهة، ودور الهرمونات وأشعة الشمس، وأفضل بروتوكولات التفتيح السريري الآمن.",
    fullInformation: {
      whatIsIt: "الكلف هو ظهور بقع وتصبغات داكنة تميل للون البني أو الرمادي، وغالباً ما تتوزع بشكل متماثل على الوجنتين، جسر الأنف، والجبهة.",
      causes: "يرتبط ظهور الكلف بشكل أساسي بالتغيرات الهرمونية (مثل الحمل أو حبوب منع الحمل) بالإضافة إلى العامل الوراثي والتعرض التراكمي المباشر لأشعة الشمس والحرارة.",
      symptoms: "بقع داكنة غير مؤلمة ولا تسبب الحكة، ولكنها تشكل تأثيراً جمالياً ومظهرياً ملحوظاً في مناطق الوجه المعرضة للضوء.",
      treatment: "البروتوكولات العلاجية الدقيقة تشمل الكريمات الموضعية المفتحة (مثل التركيبات الثلاثية المحتوية على الهيدروكينون والتريتينوين)، والتقشير الكيميائي الطبي البارد أو الليزر المدروس بإشراف دقيق من طبيب الجلدية.",
      selfCare: "الحجر الأساس في علاج الكلف والوقاية من انتكاسه هو الالتزام المطلق بوضع واقي شمس واسع الطيف (SPF 50+) وتجديده كل ساعتين عند التعرض للضوء، مع تجنب أبخرة الطهي شديدة الحرارة."
    },
    images: [
      "https://images.unsplash.com/photo-1511174511562-5f7f18bf2764?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "rosacea",
    title: "الوردية (Rosacea)",
    enTitle: "Rosacea",
    category: "الأمراض الوعائية والتهابات الوجه",
    summary: "نشرة التثقيف الصحي من BAD حول احمرار الوجه المزمن، تمدد الشعيرات الدموية، كيفية التفرقة بين الوردية وحب الشباب، والتعرف على المهيجات اليومية.",
    fullInformation: {
      whatIsIt: "الوردية هي حالة جلدية التهابية مزمنة تؤدي إلى نوبات من الاحمرار والتوهج في وسط الوجه (الأنف والخدين)، مع امتداد للشعيرات الدموية الدقيقة.",
      causes: "تحدث نتيجة اضطراب في استجابة الأوعية الدموية بالوجه والجهاز المناعي، بالإضافة إلى نشاط بعض الكائنات الدقيقة الطبيعية على البشرة (مثل طفيل الديموديكس).",
      symptoms: "احمرار دائم أو متموج في وسط الوجه، بروز شعيرات دموية دقيقة، وقد تظهر في الحالات المتقدمة بثور صغيرة وحمراء تشبه حب الشباب ولكن بدون وجود رؤوس سوداء أو بيضاء.",
      treatment: "تطبيق مستحضرات موضعية مهدئة ومضادة للالتهاب والطفيليات (مثل المترونيدازول وحمض الأزيليك أو الإيفيرمكتين)، بالإضافة إلى جلسات الليزر الوعائي (Vascular Laser) لإغلاق الشعيرات البارزة.",
      selfCare: "تجنب المحفزات الشهيرة لنوبات التوهج مثل: التعرض المباشر للشمس، المشروبات الساخنة جداً، الأطعمة الغنية بالتوابل والشطة، والتوتر العاطفي. استخدام غسول ومرطب فائق اللطف مخصص للبشرة الحساسة."
    },
    images: [
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "scabies",
    title: "الجرب الجلدي والعدوى الطفيلية (Scabies)",
    enTitle: "Scabies",
    category: "العدوى الجلدية والطفيليات",
    summary: "الدليل الطبي القاطع من BAD للقضاء التام على عدوى الجرب، فهم دورة حياة الطفيلي، أهمية العلاج الجماعي لجميع المقيمين في المنزل وبروتوكول التعقيم.",
    fullInformation: {
      whatIsIt: "الجرب هو عدوى جلدية تسببها طفيليات مجهرية (عث الجرب) تحفر أنفاقاً سطحية دقيقة في الطبقة الخارجية للجلد لوضع البيض، مما يستثير تفاعلاً تحسسياً وحكة شديدة.",
      causes: "العدوى تنتقل عن طريق التلامس الجلدي الدافئ والمباشر لفترة كافية مع شخص مصاب، أو أحياناً عبر مشاركة الملابس والمفارش والأغطية في الأماكن المشتركة.",
      symptoms: "حكة شديدة جداً ومزعجة تزداد حدتها ليلاً وعند الشعور بالدفء في الفراش، مع ظهور بثور صغيرة أو خطوط دقيقة بين أصابع اليدين، وحول المعصمين، والمرفقين، وثنايا الجلد.",
      treatment: "التطبيق الدقيق والشامل لمحلول أو كريم البيرامثرين (Permethrin 5%) أو مستحلبات بنزيل بنزوات على كامل الجسم من أسفل الرقبة وحتى أظافر قدميك وتركها للمدة المقررة ثم إعادتها بعد أسبوع.",
      selfCare: "القاعدة الذهبية للجمعية البريطانية (BAD): يجب علاج جميع أفراد الأسرة أو المقيمين بنفس المنزل في نفس اليوم وبوقت متزامن حتى لو لم يظهر عليهم الحكة بعد. غسل الملابس ومفارش الفراش بدرجة حرارة عالية لتطهيرها نهائياً."
    },
    images: [
      "https://images.unsplash.com/photo-1617897903246-719242758050?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "urticaria",
    title: "الشرية والحساسية الجلدية (Urticaria / Hives)",
    enTitle: "Urticaria (Hives)",
    category: "الأمراض الالتهابية والحساسية",
    summary: "نشرة تعريفية شاملة عن خلايا الحساسية وإفراز الهيستامين، أسباب ظهور البقع المنتفخة المفاجئة والمترددة، وخطة المتابعة مع مضادات الهيستامين الحديثة.",
    fullInformation: {
      whatIsIt: "الشرية هي طفح جلدي تحسسي مفاجئ يظهر على شكل بقع أو تورمات حمراء ووردية سطحية ومثيرة للحكة الشديدة (تشبه لسعة النحل أو ملامسة نبتة القريص).",
      causes: "تحدث عندما تقوم خلايا الجلد التناوبية (Mast Cells) بإفراز مادة الهيستامين، وذلك استجابة لمؤثرات متعددة قد تشمل: عدوى فيروسية عابرة، بعض الأدوية، بعض الأطعمة، أو تغيرات في درجات الحرارة والضغط على الجلد.",
      symptoms: "بقع حمراء متورمة ومثيرة للحكة، تتميز بظاهرة فريدة وهي (التردد): حيث تختفي البقعة من منطقة معينة في غضون ساعات قليلة لتظهر في مكان آخر من الجسم دون أن تترك أثراً.",
      treatment: "الاعتماد الأساسي هو مضادات الهيستامين (Antihistamines) الحديثة والممتدة المفعول التي لا تسبب النعاس بالجرعات التي يحددها الطبيب للسيطرة على نشاط الخلايا التحسسية.",
      selfCare: "تجنب حك الجلد باليد أو فركه بشدة لأن ذلك يستثير الخلايا لفرز المزيد من الهيستامين. تطبيق كمادات باردة ولطيفة لتهدئة تهيج الجلد والجلوس في بيئة جيدة التهوية ومريحة."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "skintags",
    title: "الزوائد اللحمية والآفات الحميدة (Skin Tags)",
    enTitle: "Skin Tags & Benign Lesions",
    category: "الزوائد الجلدية والآفات الحميدة",
    summary: "معلومات BAD الموثقة حول الزوائد الجلدية السليمة غير المؤذية التي تظهر في الثنايا والرقبة، وطرق الاستئصال الفوري السلس دون غرز أو ألم بالعيادة.",
    fullInformation: {
      whatIsIt: "الزوائد اللحمية هي نمو جلدي صغير وحميد وغير مؤذٍ على الإطلاق، يرتبط بالجلد بواسطة سويقة دقيقة ويميل للظهور في مناطق الثنايا والاحتكاك.",
      causes: "تنتج عن احتكاك الجلد بنفسه أو بالملابس والمجوهرات، وتزداد نسبة ظهورها مع التقدم في العمر، أو في حالات السمنة، أو لدى المقاومين للأنسولين.",
      symptoms: "بروزات جلدية لينة وصغيرة بلون الجلد أو أغمق قليلاً في مناطق الرقبة، الإبطين، ثنايا الفخذ، وأسفل الصدر أو حول الجفون. غير مؤلمة إلا إذا تعرضت للالتهاب أو الالتواء بسبب احتكاك شديد.",
      treatment: "إجراءات إزالة سريعة، بسيطة وبدون ألم داخل العيادة تشمل: الكي بالتبريد (النيتروجين السائل)، الكي الكهربائي الدقيق، أو التردد الحراري / الليزر دون تدخل جراحي مؤلم أو خياطة.",
      selfCare: "تجنب محاولة قص أو إزالة الزوائد في المنزل باستخدام أدوات غير طبية أو طرق تقليدية لتجنب خطر النزيف والعدوى الجلدية والندب الدائمة."
    },
    images: [
      "https://images.unsplash.com/photo-1511174511562-5f7f18bf2764?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "fungal",
    title: "العدوى الفطرية الجلدية والتينيا (Tinea / Fungal Infections)",
    enTitle: "Fungal Skin Infections (Dermatophytosis)",
    category: "العدوى الجلدية والطفيليات",
    summary: "دليل العناية والوقاية من الفطريات الجلدية (سعفة الجسد، التينيا الملونة، وفطريات القدم والدفء والرطوبة)، وبروتوكولات العلاج المضاد للفطريات.",
    fullInformation: {
      whatIsIt: "العدوى الفطرية الجلدية هي إصابة تنتج عن تكاثر الفطريات النباتية (Dermatophytes أو الخمائر) على الطبقة السطحية للجلد أو الأظافر مستغلة الكيراتين للنمو.",
      causes: "الرطوبة الزائدة، التعرق، ارتداء الملابس والأحذية غير جيدة التهوية، أو انتقال الفطريات بالملامسة من الأسطح المصابة، الأدوات الرياضية، أو الحيوانات الأليفة.",
      symptoms: "ظهور بقع دائرية حمراء ومتقشرة تتميز بوجود حافة بارزة ملتهبة ومركز أكثر نقاءً (السعفة الدائرية / Ringworm)، مع وجود حكة مستمرة أو قشور بيضاء وأطراف بنية في حالة التينيا الملونة.",
      treatment: "الكريمات والبخاخات المضادة للفطريات الموضعية (مثل الكلوتريمازول أو التيربينافين)، وفي حالات انتشار الإصابة أو التورط العميق للأظافر يتم العلاج بكبسولات مضادة للفطريات فموياً بإشراف الطبيب.",
      selfCare: "تجفيف ثنايا الجلد وبين أصابع القدم جيداً مباشرة بعد الاستحمام أو الوضوء، ارتداء ملابس وأوزان قطنية تسمح بمرور الهواء، وتجنب مشاركة المناديل أو المناشف مع الآخرين."
    },
    images: [
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "seborrheic",
    title: "إكزيما الرأس والتهاب الجلد الدهني (Seborrheic Dermatitis)",
    enTitle: "Seborrheic Dermatitis & Dandruff",
    category: "أمراض الغدد الدهنية والقشور",
    summary: "نشرة التثقيف الصحي حول القشرة المزمنة، احمرار الوجنتين والحواجب، وعلاقة خمائر الملاسيشيا بالالتهاب وكيفية السيطرة المستدامة عليها.",
    fullInformation: {
      whatIsIt: "التهاب الجلد الدهني هو اضطراب جلدي شائع ومزمن يصيب المناطق الغنية بالغدد المفرزة للزيوت (الزهم)، ويسبب قشرة فروة الرأس المزمنة واحمراراً متقشراً بالوجه.",
      causes: "ينتج عن تفاعل تحسسي والتهابي تجاه نوع من الخمائر الطبيعية (الملاسيشيا) الموجودة بشكل طبيعي على سطح الجلد الدهني، ويزداد نشاطه بوضوح مع التوتر، الشتاء، والإجهاد البدني.",
      symptoms: "قشور بيضاء أو صفراء دهنية على فروة الرأس، مصحوبة باحمرار وحكة خفيفة إلى متوسطة على جانبي الأنف، الحاجبين، خلف الأذن، وأحياناً وسط الصدر.",
      treatment: "الشامبو الطبي المحتوي على مضادات الفطريات (الكيتوكونازول أو سيلينيوم سالفيد) والقطران الطبي، بالإضافة إلى قطرات أو كريمات مضادة للالتهاب خفيفة القوة بإشراف الطبيب.",
      selfCare: "ترك الشامبو الطبي يلامس فروة الرأس لمدة 3 إلى 5 دقائق قبل شطفه بالماء الفاتر للحصول على الفاعلية القصوى. تجنب حك الفروة أو استخدام الزيوت والكريمات الثقيلة التي قد تغذي الفطريات."
    },
    images: [
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "warts",
    title: "الثآليل الجلدية والسنط الفيروسي (Viral Warts / Verruca)",
    enTitle: "Viral Warts & Verrucas",
    category: "العدوى الفيروسية الجلدية",
    summary: "دليل مرجع BAD لفهم أسباب ظهور الثآليل العادية وثآليل القدم، وكيفية انتقال فيروس الورم الحليمي (HPV)، وبروتوكولات الإزالة بالتبريد والأحماض.",
    fullInformation: {
      whatIsIt: "الثآليل (السنط) هي نتوءات جلدية خشنة وسليمة تنمو في العادة على أصابع اليدين أو أخمص القدمين نتيجة إصابة الطبقة السطحية للجلد بفيروس الورم الحليمي البشري (HPV).",
      causes: "ينتقل الفيروس عبر الملامسة الجلدية المباشرة مع سطح مصاب أو المشاركة في أدوات العناية بالأظافر والمناشف، خصوصاً إذا كان الجلد رطباً أو مصاباً بجروح طفيفة.",
      symptoms: "بروز جلدي صلب ومحكم، قد يحتوي على نقط سوداء دقيقة جداً (تسمى خطأً بجذور السنط وهي في الواقع شعيرات دموية دقيقة متصلبة). ثآليل أسفل القدم قد تسبب ألماً ملموساً عند الضغط والمشي.",
      treatment: "تطبيق الأحماض المقشرة المركزة (مثل حمض الساليسيليك)، جلسات الكي بالتبريد (النيتروجين السائل السريع)، أو العلاج بالليزر والكي الكهربائي للحالات المتكررة بالعيادة.",
      selfCare: "عدم عبث أو إزالة قشرة الثألول بالأظافر أو المقص لمنع انتشار الفيروس لمناطق مجاورة أو لأفراد الأسرة. الحفاظ على انتعال الأحذية والشباشب في المسابح وأماكن الاستحمام العامة."
    },
    images: [
      "https://images.unsplash.com/photo-1511174511562-5f7f18bf2764?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "shingles",
    title: "الحزام الناري / الهربس النطاقي (Herpes Zoster / Shingles)",
    enTitle: "Shingles (Herpes Zoster)",
    category: "العدوى الفيروسية الجلدية",
    summary: "نشرة التوعية الحيوية من BAD حول نشاط فيروس جديري الماء (العنقز) كامناً في العصب، أهمية البدء المبكر بالدواء لتجنب الآلام العصبية المزمنة.",
    fullInformation: {
      whatIsIt: "الحزام الناري هو طفح جلدي مؤلم جداً ومبصم بالحويصلات الممتدة على طول مسار عصب معين في شق واحد من الجسم أو الوجه، ويصاحبه شعور شديد بالحرقة والوخز.",
      causes: "يحدث نتيجة استيقاظ ونشاط فيروس جدري الماء (العنقز / Varicella Zoster) الذي يتواجد كامناً وهادئاً داخل العقد العصبية منذ الطفولة، وذلك عند التعرض لإجهاد جسدي شديد أو انخفاض مؤقت في المناعة.",
      symptoms: "تبدأ الأعراض بألم وحرقة شديدة ووخز بالجلد لمدة يومين إلى ثلاثة، يتبعه ظهور بقع حمراء تتحول سريعاً إلى بثور مائية مجمعة في منطقة محددة وعلى جانب واحد فقط من الجسم.",
      treatment: "الخط الذهبي هو البدء الفوري بتناول مضادات الفيروسات الفموية (مثل الأسيكلوفير أو الفالاسيكلوفير) خلال أول 72 ساعة من ظهور الطفح لتقليل حدة المرض ومنع الألم العصبي المزمن (PHN).",
      selfCare: "الحفاظ على جفاف ونظافة البثور لمنع تلوثها بالبكتيريا، تجنب الاحتكاك بالملابس الضيقة، وعدم ملامسة حديثي الولادة أو النساء الحوامل اللاتي لم يسبق لهن الإصابة بالعنقز."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1617897903246-719242758050?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "androgenetic",
    title: "تساقط الشعر النمطي والصلع الوراثي (Androgenetische Alopecia)",
    enTitle: "Androgenetic Alopecia (Pattern Hair Loss)",
    category: "أمراض الشعر والمناعة الذاتية",
    summary: "الدليل الشامل لفهم آلية تقلص بصيلات الشعر بسبب الهرمونات والجينات عند الرجال والنساء، وخيارات التحفيز المثبتة علمياً بالمينوكسيديل والعلاجات.",
    fullInformation: {
      whatIsIt: "تساقط الشعر النمطي هو السبب الأكثر شيوعاً لانحسار وترقق الشعر التدريجي عند الجنسين، ويحدث بشكل تدريجي وعلى مدى زمني طويل دون ألم أو حكة في الفروة.",
      causes: "ينتج عن استعداد جيني وراثي يرجع لتأثر بصيلات الشعر في مقدمة ووسط الرأس بهرمون (Dihydrotestosterone - DHT)، مما يؤدي لتقلص تدريجي في قطر البصيلة وقصر دورة حياتها.",
      symptoms: "عند الرجال: انحسار خط الشعر الأمامي وظهور فراغات في قمة الرأس. عند النساء: احتفاظ خط الشعر الأمامي بمكانه مع ترقق واتساع في الفرق المنتصف لفروة الرأس.",
      treatment: "المحاليل الموضعية المحفزة للنمو (المينوكسيديل Minoxidil)، المحاليل المضادة للهرمون الموضعية، العلاجات الدوائية الفموية التي يصفها الطبيب، وجلسات الحقن المغذية أو الميزوثيرابي والبلازما البيولوجية.",
      selfCare: "الالتزام بالاستمرارية؛ فالإنبات والتحفيز يتطلب ما لا يقل عن 3 إلى 6 أشهر من الانتظام اليومي لملاحظة النتائج. التعامل بلطف مع الشعر وتجنب التسريحات الشادة للبصيلات (Traction Alopecia)."
    },
    images: [
      "https://images.unsplash.com/photo-1606902965551-dce093cda6e7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "lichen_planus",
    title: "مرض الحزاز المسطح (Lichen Planus)",
    enTitle: "Lichen Planus",
    category: "الأمراض المناعية المزمنة",
    summary: "نشرة تعريفية عن الطفح اللامع المائل للبنفسجي المسبب للحكة، ارتباطه بالنشاط المناعي، وطرق التعامل مع إصابات الجلد والأغشية المخاطية وتجنب الأثر.",
    fullInformation: {
      whatIsIt: "الحزاز المسطح هو اضطراب جلدي التهابي غير معدٍ يصيب الجلد، الأغشية المخاطية للفم، والأظافر، ويظهر على شكل بقع أو حطاطات لامعة تميل للون البنفسجي أو الوردي.",
      causes: "يعد من الأمراض ذاتية المناعة العابرة التي يستثير فيها جهاز المناعة لمهاجمة خلايا الطبقة القاعدية للجلد، وقد يتقارب مع تناول بعض الأدوية أو التوتر الشديد أو التهابات الكبد في نسبة طفيفة من المرضى.",
      symptoms: "حكة حادة ومزعجة، ظهور حبوب أو بقع مسطحة ذات لمعان مميز على باطن الرسغين، الكاحلين، وأسفل الظهر، مع إمكانية ظهور خطوط شبكية بيضاء داخل تجويف الفم.",
      treatment: "المراهم والكريمات الستيروئيدية الموضعية القوية، مضادات الهيستامين لتخفيف الحكة الليلية، والعلاجات المناعية والضوئية في حال انتشار الإصابة.",
      selfCare: "تجنب فرك أو تجريح المناطق المصابة لمنع تحفز بقع جديدة، والالتزام بمرطبات مهدئة للجلد. في حال وجود إصابة فمية يوصى بتجنب الأطعمة الحمضية والتوابل والحفاظ على نظافة الفم اللطيفة."
    },
    images: [
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "contact_dermatitis",
    title: "إكزيما التلامس التحسسية والمهنية (Contact Dermatitis)",
    enTitle: "Contact Dermatitis",
    category: "الأمراض الالتهابية والحساسية",
    summary: "دليل مرجع BAD حول التحسس الموضعي الناتج عن المعادن كالنيكل، المنظفات المنزلية، والأصباغ، وطرق إجراء اختبارات التلامس الطبية وتدابير الوقاية.",
    fullInformation: {
      whatIsIt: "إكزيما التلامس هي التهاب وتهيج جلدي ينتج عن ملامسة البشرة المباشرة لمادة خارجية إما أن تكون مادة كيميائية مهيجة أو مادة مسببة للحساسية.",
      causes: "التحسس من معادن الإكسسوارات والمجوهرات (مثل النيكل)، المنظفات والمذيبات القوية، صبغات الشعر، عطور المستحضرات، أو القفازات المطاطية (اللاتكس).",
      symptoms: "احمرار وتورم شديد ومؤطر في منطقة التلامس تماماً (مثل حول زر البنطال أو أذن الحلق أو اليدين)، مع ظهور قشور وجفاف شقاقي وحكة حادة.",
      treatment: "الإجراء الجوهري هو تحديد العامل المسبب وتجنبه تماماً. التداوي يتم عبر المراهم والكريمات الستيروئيدية المضادة للالتهاب والمطريات الطبية المكثفة لترميم الحاجز الجلدي.",
      selfCare: "ارتداء قفازات قطنية مبطنة بالداخل عند التعامل مع المنظفات المنزلية. استبدال الصابون والمنظفات القوية ببدائل صابونية طبية لطيفة وخالية تماماً من العطور والمواد المحسسة."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "hidradenitis",
    title: "التهاب الغدة العرقية المقيح (Hidradenitis Suppurativa - HS)",
    enTitle: "Hidradenitis Suppurativa (Acne Inversa)",
    category: "أمراض الغدد الدهنية والتهاباتها",
    summary: "معلومات BAD الطبية حول ظهور العقيدات والدمامل المتكررة في الثنايا، كيفية تقليل نوبات الألم، ودور العلاجات الحديثة والتعاطي السليم مع المرض.",
    fullInformation: {
      whatIsIt: "التهاب الغدة العرقية المقيح هو مرض جلدي التهابي مزمن يصيب بصيلات الشعر في أماكن تواجد الغدد العرقية المفروزة، ويظهر في ثنايا الجلد كالإبطين والمنطقة المغبنية وأسفل الثديين.",
      causes: "ينتج عن انسداد عنق بصيلة الشعر مسبباً التورم والالتهاب ثم انفجار البصيلة بالداخل، ويرتبط بالعامل الوراثي والتأثر الهرموني، ويُفاقمه بشكل واضح التدخين وزيادة الوزن.",
      symptoms: "ظهور عقيدات صلبة وحمراء مؤلمة تحت الجلد، قد تتحول إلى خراجات تتفتح لتفرز سائلاً صديدياً وتلتئم مشكرة مسالك وأنفاقاً تحت الجلد أو ندباً ملموسة.",
      treatment: "الغسولات المطهرة الموضعية، المضادات الحيوية الموضعية أو المرتكزة لفترات أطول لخصائصها المضادة للالتهاب، وفي الحالات المستعصية العلاجات البيولوجية الحديثة تحت المراجعة التخصصية.",
      selfCare: "الإقلاع الفوري عن التدخين، الابتعاد الكامل عن الحلاقة بالموس الحاد في أماكن الإصابة والاعتماد على إزالة الشعر بالليزر عند السيطرة، وارتداء ملابس قطنية واسعة وفضفاضة لمنع الاحتكاك."
    },
    images: [
      "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "moles_nevi",
    title: "مراقبة الشامات والوحمات الجلدية (Moles & Nevi - ABCDE Rule)",
    enTitle: "Moles & Melanocytic Nevi Monitoring (ABCDE)",
    category: "أمراض التصبغات والجلد",
    summary: "الدليل الذهبي للمريض من مرجع BAD لمعرفة العلامات التحذيرية الخمس في الشامات والوحمات الجلدية (ABCDE)، وأهمية الفحص السريري بالدرماتوسكوب.",
    fullInformation: {
      whatIsIt: "الشامات (الخال أو الوحمات الصبغية) هي تجمعات طبيعية وشائعة لخلايا الميلانين الصبغية في طبقات الجلد، وتكون عادة حميدة ومستقرة تماماً طوال الحياة.",
      causes: "تتكون نتيجة تجمع وتكاثر الخلايا الصبغية في نقاط محددة بدلاً من توزعها بالتساوي، وتزداد أعدادها بفضل الجينات الوراثية أو التعرض المبكر لكثافة أشعة الشمس في سنوات الطفولة.",
      symptoms: "بقع أو نتوءات بنية، داكنة، أو وردية اللون ذات حدود متناسقة وحجم مستقر لا يتغير بسرعة. لا تسبب أي ألم أو حكة أو نزيف.",
      treatment: "الشامات الطبيعية لا تتطلب أي علاج طبي على الإطلاق. يتم إجراء الفحص المنظم في العيادة باستخدام جهاز الدرماتوسكوب (Dermatoscopy) للتأكد من سلامتها ومتابعتها بأمان.",
      selfCare: "تطبيق قاعدة الفحص الذاتي الشهيرة (ABCDE) ومراجعة الطبيب في حال ظهور: A- عدم التماثل في شقّي الشامة، B- عدم انتظام أو شرشرة الحدود، C- تعدد أو تغير الألوان بداخلها، D- قطر أكبر من 6 ملم، E- التطور أو التغير الملحوظ في الشكل أو الحجم أو ظهور الحكة والنزيف."
    },
    images: [
      "https://images.unsplash.com/photo-1511174511562-5f7f18bf2764?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "hyperhidrosis",
    title: "فرط التعرق الموضعي (Hyperhidrosis)",
    enTitle: "Primary Focal Hyperhidrosis",
    category: "أمراض الغدد العرقية",
    summary: "نشرة طبية مفصلة عن فرط التعرق الزائد في الإبطين وراحتين اليد، خيارات مضادات التعرق الطبية المركزة، وبروتوكولات العلاج الحديثة بالحقن.",
    fullInformation: {
      whatIsIt: "فرط التعرق هو حالة ينتج فيها الجسم كميات من العرق تفوق بكثير ما يتطلبه تنظيم درجة الحرارة الطبيعية للجسم، ويحدث بشكل رئيسي في راحتي اليدين، الإبطين، وأخمص القدمين.",
      causes: "غالباً ما يكون فرط تعرق أولي ناتج عن استثارة مفرطة وغير مبررة للأعصاب المغذية للغدد العرقية دون ارتباط بمرض عضوي آخر، وقد يرتبط بتاريخ عائلي وراثي.",
      symptoms: "تعرق غزير وملحوظ في مواضع محددة بالتناظر على الجانبين، يؤثر على ممارسة الحياة اليومية والكتابة باليد أو المظهر الاجتماعي للملابس، ويتميز غالباً بأنه يتوقف تماماً أثناء النوم في الليل.",
      treatment: "مضادات التعرق الطبية المعتمدة على كلوريد الألومنيوم التركيزي المرتفع (تطبق ليلاً على جلد جاف تماماً)، أو الحقن الموضعي الدقيق لمهدئات العصبي (البوتكس) لتعطيل إفراز العرق لشهور ممتدة بأمان وكفاءة عالية بالعيادة.",
      selfCare: "ارتداء ملابس داخلية قطنية طبيعية فضفاضة تمتص الرطوبة، تجنب الأقمشة والألياف الصناعية المحتبسة للحرارة، واستخدام مستحضرات الغسول اللطيفة لتقليل نمو البكتيريا المسؤولة عن الرائحة."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "molluscum-contagiosum",
    title: "المليساء المعدية الفيروسية (Molluscum Contagiosum)",
    enTitle: "Molluscum Contagiosum",
    category: "الأمراض الفيروسية والعدوى",
    summary: "نشرة مفصلة عن حبوب المليساء اللؤلؤية المعدية التي تظهر على سطح الجلد لدى الأطفال والبالغين، طرق تفادي انتشارها بالعدوى التلامسية وخيارات التعامل الطبي.",
    fullInformation: {
      whatIsIt: "المليساء المعدية هي عدوى فيروسية سطحية وحميدة جداً تصيب الجلد، وتتميز بظهور حبوب أو نتوءات صغيرة قبة الشكل ولؤلؤية اللون، وغالباً ما تتوسطها نقرة صغيرة في مركز الحبة.",
      causes: "تحدث العدوى بسبب فيروس ينتمي لعائلة جدري الماء (Poxvirus)، وينتقل بسلاسة من شخص لآخر عبر التلامس الجلدي المباشر، أو مشاركة المناشف والأدوات الشخصية وفي المسابح وحمامات السباحة.",
      symptoms: "ظهور نتوءات جلدية صلبة ووردية أو بلون الجلد الطبيعي (يتراوح حجمها بين 2-5 مم)، قد تكون مركزية ملساء وتسبب حكة طفيفة، لكنها غير مؤلمة في العادة وتنتشر عند حكها إلى مناطق مجاورة.",
      treatment: "في الغالب لدى الأطفال يتعافى جهاز المناعة من الحبوب تلقائياً خلال عدة أشهر دون الحاجة لتدخل مؤلم. أما في الحالات الممتدة والبالغين فيمكن التخلص منها علاجياً بالتجميد (النيتروجين السائل)، أو التطبيق الموضعي لمحاليل التقشير الطبية بالعيادة.",
      selfCare: "الامتناع عن عصر أو إخراج محتويات الحبوب تجنبًا للندبات ولتفادي تفشي الفيروس، تخصيص منشفة وأدوات خاصة للمصاب في المنزل، وتغطية الحبوب بضماد خفيف وبسيط أثناء التمارين الرياضية أو الأنشطة المدرسية."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "actinic-keratosis",
    title: "التقرن الشعاعي وآفات الشمس المزمنة (Actinic Keratosis)",
    enTitle: "Actinic Keratosis & Solar Damage",
    category: "آفات الشمس والمراقبة الجلدية",
    summary: "دليل إرشادي لمعرفة البقع الجلدية الخشنة والمتقشرة الناتجة عن تراكم أضرار أشعة الشمس المزمنة على الوجه واليدين، وأهمية علاجها الوقائي لحماية الجلد.",
    fullInformation: {
      whatIsIt: "التقرن الشعاعي عبارة عن بقع جلدية خشنة الملمس ومتقشرة تنمو في المناطق التي تتعرض بانتظام لسنوات طويلة لأشعة الشمس المباشرة، وتُصنَّف كآفات ما قبل سرطانية تتطلب المتابعة والعلاج الوقائي.",
      causes: "التراكم المزمن والمتزايد للأضرار الناتجة عن الأشعة فوق البنفسجية (UV) القادمة من الشمس أو جلسات التسمير الصناعي (Tanning)، خاصة لدى ذوي البشرة الفاتحة أو المهن الميدانية في أوقات الذروة.",
      symptoms: "بقعة أو بقع سطحية حمراء أو بنية فاتحة ملمسها خشن كـ (ورق الصنفرة)، قد تختفي القشور مؤقتاً لتظهر مجدداً في نفس الموضع، وتترافق أحياناً مع وخز أو الشعور بالاحتكاك والغض.",
      treatment: "العلاج بالتجميد السريع بالنيتروجين السائل للآفات الفردية، أو العلاج الميداني للمنطقة التالفة باستخدام كريمات المناعة والتقشير الكيميائي (مثل الإيميكويمود أو الفلورو يوراسيل) أو جلسات العلاج الضوئي الديناميكي (PDT).",
      selfCare: "التطبيق الصارم واليومي لواقي الشمس الطبي (SPF 50+)، ارتداء القبعات ذات الحواف العريضة والملابس الواقية في الخارج، وفحص دوري ذاتي للتعرف على أي ازدياد سريع في سمك أو بروز البقع الجلدية."
    },
    images: [
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "stasis-dermatitis",
    title: "إكزيما الركود الوريدي وتقرحات الساق (Stasis Dermatitis & Leg Ulcers)",
    enTitle: "Stasis Dermatitis & Venous Ulceration",
    category: "الأمراض الوعائية وأطراف الجسم",
    summary: "نشرة متخصصة عن الالتهابات الجلدية والتصبغات الرمادية المزمنة في أسفل الساقين المصاحبة لدوالي الأوردة والركود الدموي، وبروتوكولات الجوارب الضاغطة والعناية الجلدية.",
    fullInformation: {
      whatIsIt: "إكزيما الركود الوريدي (أو التهاب الجلد الجاذبية) هي حالة تظهر بشكل رئيسي حول الكاحلين وأسفل الساقين نتيجة ضعف الصمامات الوريدية واعتلال الدورة الدموية مما يسبب تسريب وتجمع السوائل في الجلد.",
      causes: "القصور الوريدي المزمن ودوالي القدمين، حيث تتدهور مرونة الأوردة التي تنقل الدم من الأرجل إلى القلب، ما يؤدي لزيادة الضغط الموضعي وتسرب كريات الدم التالفة نحو الأنسجة الجلدية وتحفز الالتهاب والحكة.",
      symptoms: "انتفاخ وتورم بأسفل الساق يتحسن عند الاستلقاء ورفع العضل، ترقق الجلد مع حكة وجفاف شديدين، ظهور تغيرات في لون الجلد إلى الأرجواني أو الرمادي والبني الداكن، وقد تتشكل تقرحات مفتوحة إن أهملت الحك.",
      treatment: "الاعتماد المباشر على التخفيف الالتهابي عبر مراهم الكورتيزون الموضعية المناسبة لفترات محدودة، بالإضافة للعلاج الهام والرئيسي وهو العلاج بالضغط (الجوارب الضاغطة الطبية أو اللفائف المتخصصة) لتنشيط الدورة الوريدية.",
      selfCare: "رفع الساقين فوق مستوى الأريكة أو القلب مرتين لثلاث مرات يومياً وأثناء النوم، الترطيب اللطيف والمكثف بزيت أو كريمات عازلة غير عطرية لتلافي تشققات حاجز البشرة، وممارسة المشي المنتظم لتعزيز مضخة عضلات السمانة."
    },
    images: [
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "pityriasis-rosea",
    title: "النخالة الوردية (Pityriasis Rosea)",
    enTitle: "Pityriasis Rosea",
    category: "الأمراض الطفحية والالتهابية",
    summary: "نشرة تعريفية للطفح الجلدي الوردي العابر الذي يمتاز بظهور (البقعة الأم) الأولية ثم انتشاره على الصدر والظهر، وطريقة إدارة الحكة المؤقتة.",
    fullInformation: {
      whatIsIt: "النخالة الوردية هي طفح جلدي حميد وفجائي وشائع، يصيب في الغالب فئة الشباب والمراهقين، ويمتاز بدرجة أمانه العالية وأنه لا يترك نتوءات أو تلفيات دائمة بعد التعافي الذاتي منه.",
      causes: "السبب الدقيق غير معروف بصورة نهائية، لكنه يرتبط بقوة باستجابة الجهاز المناعي لعدوى فيروسية سطحية عادية (تتعلق غالباً بسلالات غير معدية من فيروس الهربس البشري)، وهو مرض غير معدٍ بين الأفراد.",
      symptoms: "تبدأ الحالة غالباً بظهور بقعة دائرية واحدة واضحة الحواف أكبر قليلاً في البطن أو الظهر تُدعى (البقعة الأم Herald Patch)، وبعد أسبوع أو اثنين ينتشر طفح وردي متقشر بشكل بيضاوي شبيه بتنظيم (فروع شجرة الصنوبر) على منطقة الجذع.",
      treatment: "بما أنه مرض يختفي ويشفى تلقائياً في فترة تتراوح بين 4 إلى 8 أسابيع، يُقتصر العلاج على تهدئة الحكة والأعراض المصاحبة باستخدام محاليل اللوشن المهدئة (Calamine)، ومضادات الهيستامين، أو كريمات الستيرويد الخفيفة للحكة.",
      selfCare: "الاستحمام بماء معتدل الحرارة أو مائل للبرودة لأن الماء الساخن يهيج الحكة ويزيد احمرار الطفح، واختيار صابون مرطب خالي من الرغاوي والمواد العطرية الصارمة مع الصبر والاطمئنان لمراحل اختفاء البقع."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "discoid-lupus-erythematosus",
    title: "الذئبة الحَمراء الجلدية القرصية (Discoid Lupus Erythematosus - DLE)",
    enTitle: "Discoid Lupus Erythematosus (DLE)",
    category: "أمراض المناعة الذاتية الجلدية",
    summary: "دليل طبي لفهم الذئبة الجلدية القرصية التي تسبب بقعاً وتقرحات قرصية حمراء متقشرة على الوجه وفروة الرأس، وضرورة الحماية الوقائية الصارمة من الشمس.",
    fullInformation: {
      whatIsIt: "الذئبة الجلدية القرصية هي مرض التهابي مزمن يسببه نشاط زائد في مناعة الجسم الذاتية يتركز في الأغلب على أجزاء الجلد الخارجية المعرضة للشمس، ويختلف كلياً عن الذئبة الحمراء الجهازية التي تؤثر على الأعضاء الداخلية الكاملة.",
      causes: "استجابة غير منتظمة من خلايا المناعة لمهاجمة أنسجة البشرة السطحية، ويعد التعرض لأشعة الشمس المباشرة والأشعة فوق البنفسجية هو المفتاح الرئيسي والأكثر تحفيزاً لاندلاع واشتعال التقرن والتهابات الآفات.",
      symptoms: "ظهور لويحات أو بقع حمراء قرصية الشكل متقشرة ومحددة الحواف على الوجه، الوجنتين، الأنف، أو فروة الرأس؛ قد تسبب في حال التخلف عن العلاج تساقطاً مناعياً دائماً للشعر أو حدوث ضمور وندبة فاتحة أو داكنة بالبشرة.",
      treatment: "الكريمات الستيرويدية الموضعية أو الستيرويدات تحت الجلد عالية القوة للسيطرة على النشاط الالتهابي الحاد، مع الاستخدام الطبي المنظم للأدوية الفموية المضادة للملاريا (مثل الهيدروكسي كولوروكين) لتحجيم الاستجابة المناعية للحرارة والضوء.",
      selfCare: "الحماية المطلقة والدائمة من ضوء الشمس المباشر والمنعكس عبر وضع كريمات الواقي الضوئي الطبية واسعة الطيف (UVA/UVB)، الالتزام بالملابس العازلة، والامتناع التام عن التدخين لأنه يعيق فاعلية الأدوية ويزيد توהج المرض."
    },
    images: [
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "pemphigus-pemphigoid",
    title: "الفقاع الجلدي والفقاعاني المناعي (Pemphigus & Pemphigoid)",
    enTitle: "Immunobullous Diseases (Pemphigoid)",
    category: "أمراض الفقاعات الجلدية المناعية",
    summary: "نشرة توضيحية لحالات الفقاعات والفقاعاني المناعي التي ينتج عنها فقاعات مائية وتآكل سطحي في الجلد والأغشية المخاطية، وبروتوكولات المثبطات الطبية.",
    fullInformation: {
      whatIsIt: "مجموعة من الأمراض المناعية الذاتية السريرية التي يفرز فيها جهاز المناعة عن طريق الخطأ أجساماً مضادة تهاجم الروابط الهيكلية الدقيقة التي تشد طبقات الجلد ببعضها البعض، مسببة ظهور فقاعات سائلة متوترة.",
      causes: "خلل في المراقبة المناعية يؤدي لإنتاج البروتينات المضادة للمواد اللاصقة بين الخلايا الكيراتينية في الأدمة أو الغشاء القاعدي للجلد (Desmosomes أو Hemidesmosomes)، ولا يعتبر مرضاً معدياً للآخرين.",
      symptoms: "ظهور حويصلات وفقاعات مائية كبيرة أو سطحية مشدودة على اليدين، الأطراف والبطن، تتلوها تقرحات وتقشر عند انفجار الفقاعة، وقد تظهر تقرحات وتسلخات شديدة داخل تجويف الفم والأغشية المخاطية في أنواع الفقاع.",
      treatment: "يتضمن البرتوكول العلاجي استخدام الستيرويدات الكورتيزونية عن طريق الفم وفي العيادات بجرعات علاجية منضبطة لتهدئة الهجمة، مدعومة بمثبطات المناعة المساعدة (مثل الريتوكسيماب أو الآزَاثيوبرين) لاستقرار الحالة وبناء الجلد.",
      selfCare: "تجنب الأطعمة الخشنة والساخنة أو الأحماض إذا كانت تقرحات الفم متواجدة، ارتداء أقمشة قطنية باردة ونظيفة لا تلتصق بالجلد، واستعمال ضمادات معقمة وغير لاصقة تحت إشراف طاقم الرعاية السريري."
    },
    images: [
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "cellulitis-erysipelas",
    title: "التهاب النسيج الخلوي والحمرة الجلدية (Cellulitis & Erysipelas)",
    enTitle: "Cellulitis & Erysipelas (Bacterial Infection)",
    category: "الأمراض البكتيرية والعدوى السيسيّة",
    summary: "نشرة طبية للتعرف على أعراض الالتهاب البكتيري الحاد في الأنسجة الجلدية العميقة والسطحية (الاحمـرار التورم الساخن) وأهمية العلاج الحيوي السريع.",
    fullInformation: {
      whatIsIt: "التهاب النسيج الخلوي هو عدوى بكتيرية حادة تصيب طبقات الجلد العميقة والأنسجة الدهنية التحتية، بينما تُعرف (الحُمرة - Erysipelas) بالعدوى البكتيرية السطحية مع حواف مرتفعة شديدة الاحمرار والالتهاب.",
      causes: "دخول سلالات من بكتيريا العقدية (Streptococcus) أو العنقودية (Staphylococcus) إلى الأنسجة الداخلية عبر جرح طفيف، خدش جلدي، تشققات قدم الرياضي بين الأصابع، أو لسعات الحشرات في اليد أو القدم.",
      symptoms: "بقعة جلدية حمراء شديدة السخونة والتورم والملمس المؤلم عند الضغط العادي، تتسع مساحتها مع الساعات، وقد يصاحبها ارتفاع في حرارة الجسم وإجهاد عام (حمى ورجفة) وتورم في العقد الليمفاوية القريبة.",
      treatment: "المضادات الحيوية الطبية المقررة سريرياً عن طريق الفم بالجرعات الدقيقة لمدة 7 إلى 14 يوماً، أو عن طريق الوريد في الحالات الشديدة للسيطرة الحيوية على البكتيريا ومنع امتدادها للأوردة.",
      selfCare: "إبقاء المنطقة المصابة (الساق أو الطرف) مرفوعة جيداً لتقليل احتباس وتجمع السائل الانتفاخي، غسل الجروح الطفيفة فوراً وتطهيرها بالمعقمات المناسبة، ومراقبة الحدود الحمراء للتأكد من تراجعها بفضل المضاد الحيوي."
    },
    images: [
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "vascular-nevi",
    title: "الوحمات الدموية والشعيرات العنكبوتية (Hemangiomas & Spider Naevi)",
    enTitle: "Vascular Malformations & Spider Naevi",
    category: "التمددات الوعائية والوحمات الحميدة",
    summary: "نشرة تفصيلية للوحمات الدموية الوردية والشعيرات الدموية العنكبوتية المتكاثرة بالوجه والجسم، وأحدث بروتوكولات إزالتها الانتقائية بجلسات الليزر الوعائي.",
    fullInformation: {
      whatIsIt: "الشعيرات العنكبوتية هي تمددات سطحية دقيقة وموضعية لوعاء دموي صغير تظهر مع شبكة من الروافد الحمراء حول نقطة مركزية تشبه بيت العنكبوت، بينما الوحمات الدموية هي تجمع حميد وسالم لخلايا الأوعية الدموية بالبشرة.",
      causes: "الاستعداد الوراثي، التغيرات الهرمونية الشديدة (كمراحل الحمل)، التعرض المتكرر لأشعة الشمس والتقلبات الجوية القاسية، أو في بعض حالات القصور الكبدي، وأحياناً تظهر تلقائياً دون سبب مرضي.",
      symptoms: "بقعة أو نقطة حمراء صغيرة زاهية ومحددة تنتشر حولها خطوط شعرية حمراء دقيقة متعرجة على الخدين، أو وحمة حمراء فراولية اللون ومستديرة لا تؤلم وتتميز بأنها تبهت (تبيض مؤقتاً) عند الضغط بالأصبع عليها.",
      treatment: "في معظم الأحيان تكون مشكلة جمالية بحتة لا تحتاج بالضرورة لجراحة؛ ويُعد العلاج بالليزر الوعائي الموجه (مثل ليزر PDL أو ليزر KTP السريع) أو الكي الكهربائي الدقيق هو الحل الجذري والآمن لإتلافها دون أثر.",
      selfCare: "تلافي دعك ومسح بشرة الوجه بالمقشرات الكيميائية القوية أو الليف القاسي الذي يمزق الأوعية التوسعية الرهيفة، حماية البشرة بالمرطبات الواقية، والابتعاد عن السخونة البالغة وأشعة الشمس والساونا المتكررة."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "ichthyosis-vulgaris",
    title: "مرض السماك الجلدي الشائع (Ichthyosis Vulgaris)",
    enTitle: "Ichthyosis Vulgaris (Dry Scaly Skin)",
    category: "الأمراض الوراثية والتقشير الجلدي",
    summary: "دليل خاص بالمرض الجلدي الوراثي الذي يسبب جفافاً متقشراً وشديداً للجلد يشبه (حراشف السمك)، وبروتوكولات الترطيب الفوري بمقشرات اليوريا وحمض اللبنيك.",
    fullInformation: {
      whatIsIt: "مرض السماك الجلدي الشائع هو اضطراب وراثي يحد من قدرة خلايا الجلد الميتة على التقشر والتآكل بمعدلها الفعلي الطبيعي، مما يسبب تراكم قشور جافة وصلبة على السطح تبدو كحراشف أو قشور السمك.",
      causes: "طفرة وراثية تؤدي لنقص أو غياب إنتاج بروتين حيوي في الطبقة الخارجية يُدعى (الفيلاغرين - Filaggrin)، مما يضعف احتباس الماء والرطوبة ويعجز البشرة عن التساقط الانسيابي للخلايا الكيراتينية الميتة.",
      symptoms: "جفاف جلدي فائق مع قشور دقيقة بلون أبيض، رمادي أو بني فاتح، تغطي بصورة ملحوظة الجهة الخارجية من الساقين ومقدمة الذراعين، وخطوط جلدية بارزة وعميقة في راحتي اليد، مع تحسن صيفي وانتكاسة شتوية واضحة.",
      treatment: "الكريمات المرطبة الطبية عالية الفاعلية التي تشتمل على مواد مقشرة لكيرياتين الجلد (مثل كريمات اليوريا Urea بتركيز 10%-20%، حمض الساليسيليك، أو حمض اللبنيك Lactic Acid) لتكسير الروابط وإذابة الحراشف الملتصقة.",
      selfCare: "التشرب والدهان السريع والمكثف بالمرطبات الغنية فور الاستحمام خلال 3 دقائق والجلد لا يزال ندياً ورطباً لحجب الماء بالداخل، تجنب المياه الفوارة الساخنة والمقشرات القاسية، والاستحمام اللطيف بصابون مرطب زيتي."
    },
    images: [
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "folliculitis",
    title: "التهاب بصيلات الشعر ونمو الشعر تحت الجلد (Folliculitis)",
    enTitle: "Folliculitis & Pseudofolliculitis Barbae",
    category: "أمراض البصيلات والشعر",
    summary: "نشرة طبية للتعامل مع البثور المتهيجة والملتهبة في بصيلات الشعر بعد الحلاقة أو التعرق، وحلول التطهير الموضعي وإلغاء تهيج الشعر المنتكس بالليزر.",
    fullInformation: {
      whatIsIt: "التهاب بصيلات الشعر هو التهاب أو عدوى تصيب جيوب البصيلات، أما حالة (Pseudofolliculitis Barbae) فهي رد فعل التهابي حاد لنمو الشعرة وانعكاسها وانغراسها داخل الأدمة بدلاً من انطلاقها لخارج الجلد بعد القص أو الحلاقة.",
      causes: "عدوى بكتيرية عنقودية أو فطرية بسبب انسداد مسام الشعر وتعرضها للرطوبة والاحتكاك بالملابس الضيقة، أو انحناء الشَعرة الخشنة والمتموجة ونموها تحت السطح الجلدي عند استخدام شفرات الحلاقة المزدوجة والمتعددة.",
      symptoms: "ظهور بثور وحبوب حمراء صغيرة أو برؤوس صفراء متقىحة عند بوابات بصيلات الشعر في الوجه (الذقن والرقبة)، الظهر، الأفخاذ، أو الأرداف، مصحوبة بحكة مزعجة أو الشعور باللسع والتحسس الموضعي عند ملامستها.",
      treatment: "الغسولات المطهرة المعتمدة على الكلورهيكسيدين أو بنزويل بيروكسيد، والمضادات الحيوية الموضعية البسيطة (مثل الموبيروسين أو الفيوسيدين)، فيما يُعتبر العلاج بإزالة الشعر الدائم بالليزر (Laser Hair Removal) هو الحل الذهبي لإنهاء معاناة نمو الشعر تحت الجلد.",
      selfCare: "الحلاقة باتجاه نمو الشعر الفعلي وليس العكس مع شفرة فردية معقمة وبدون تمرير متكرر وقاطع على نفس النقطة، ارتداء ملابس رياضية فضفاضة تسمح بالتنفس وتغييرها فور انتهاء التعرق الرياضي أو المجهود العضلي."
    },
    images: [
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "dermatitis-herpetiformis",
    title: "التهاب الجلد الهربسي وعلاقته بحساسية القمح (Dermatitis Herpetiformis)",
    enTitle: "Dermatitis Herpetiformis (Celiac-Associated Skin Disease)",
    category: "الأمراض المناعية والفقاعية",
    summary: "دليل إرشادي شامل حول الطفح الجلدي التقرحي وشديد الحكة المرتبط بالتحسس الغلوتيني (مرض السيلياك)، وطرق الضبط الغذائي الدقيق والدوائي.",
    fullInformation: {
      whatIsIt: "التهاب الجلد الهربسي هو مظهَر جلدي مناعي ذاتي ومزمن يتميز بظهور فقاعات وبثور دقيقة شديدة الحكة؛ ويُعتبر الانعكاس الجلدي المباشر لمرض السيلياك (التحسس الدائم لمادة الغلوتين في الأمعاء الدقيقة) حتى وإن لم تظهر أعراض هضمية واضحة.",
      causes: "استجابة مناعية غير طبيعية وتنتج عند تناول الغلوتين (البروتين الموجود في القمح، الشعير، والجاودار)؛ حيث ترتبط الأجسام المضادة من نوع (IgA) وتترسب في الطبقات الحليمية تحت البشروية الجدلية مسببة استدعاء خلايا المناعة البيضاء والتهاباً شديداً.",
      symptoms: "بثور وتقرحات حمراء صغيرة متجمعة تشبه عناقيد الهربس، تترافق مع حكة حارقة ومكثفة ومؤلمة أحياناً، وتتركز بشكل تناظري على النتوءات العظمية مثل المرفقين، الركبتين، الأرداف، وأسفل الظهر وفروة الرأس.",
      treatment: "العلاج الدوائي الأساسي للتحكم السريع في الحكة هو عقار (Dapsone - دابسون) أو السلفابيريدين تحت إشراف طبي مع فحص مستويات الأنزيمات الدموية؛ ويعتبر الالتزام التام بنظام غذائي خالٍ تماماً من الغلوتين (Gluten-Free Diet) العلاج الشافي والمؤكد على المدى الطويل.",
      selfCare: "فحص كافة ملصقات ومكونات الأطعمة المستهلكة بدقة متناهية لتجنب تلوث الأكل بالغلوتين، تجنب العبث بالبثور أو حكّها لمنع حدوث الندبات والعدوى البكتيرية الثانوية، والاستعانة باختصاصيين للتغذية العلاجية للمحافظة على امتصاص المعادن والفيتامينات الأساسية."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "keloids",
    title: "الندبات الجدرية وتضخم الأنسجة (Keloids & Hypertrophic Scars)",
    enTitle: "Keloids & Hypertrophic Scars",
    category: "التليف والندبات الجلدية",
    summary: "نشرة مفصلة للتمييز بين الندبات المتضخمة والندبات الجدرية المتنامية خارج حدود الجرح، وأحدث تقنيات الحقن والضغط والعلاجات بالليزر.",
    fullInformation: {
      whatIsIt: "الندبة الجدرية (Keloid) هي تضخم ليفي مستمر لأنسجة ندبة الجلد لا يتوقف عند حدود الإصابة الأصلية بل يمتد للأنسجة السليمة المحيطة؛ بينما الندبة المتضخمة (Hypertrophic Scar) تظل مرتفعة وسميكة ولكنها تلتزم بحدود الجرح الأصلي.",
      causes: "نشاط مفرط وغير منضبط للأرومات الليفية (Fibroblasts) مما يؤدي لإفراز وإنتاج مرضي لكولاجين من النوع الأول والنوع الثالث أثناء عملية التئام الجروح، وغالباً ما يحصل بعد حب الشباب، جروح الحروق، الغرز الرياضية والطبية، أو ثقب الأذن مع وجود عامل وراثي واستعداد جيني.",
      symptoms: "كتلة صلبة أو مطاطية ناتئة بلون أحمر أو وردي لامع أو داكن، قد تكون مؤلمة عند اللمس أو مصحوبة بحكة مزودة ونبض موضعي ينمو تدريجياً، وغالباً ما تشيع في الصدر، منتصف الأكتاف، شحمة الأذن، والظهر.",
      treatment: "الحقن الموضعي المنتظم بمركبات الستيرويد (Triamcinolone) داخل الندبة لإذابة الكولاجين وتسطيحه، العلاج بالتجميد بالنيتروجين السائل (Cryotherapy)، الليزر الوعائي (Pulsed Dye Laser) لتخفيف الاحمرار والحكة، أو الاستئصال الجراحي المتبوع مباشرة بالعلاج الإشعاعي السطحي لتجنب عودة النمو.",
      selfCare: "استخدام صفائح السيليكون الطبية الضاغطة (Silicone Gel Sheets) لمدة 12-24 ساعة يومياً لعدة أشهر، حماية الندبات الحديثة من التعرض لشد مفرط أو الاحتكاك، وتجنب الإجراءات غير التجميلية أو ثقب الجلد عند ذوي الاستعداد الوراثي لتشكيل الجدرات."
    },
    images: [
      "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "impetigo",
    title: "القوباء أو الحصف البكتيري المعدي (Impetigo)",
    enTitle: "Impetigo & Contagious Skin Infections",
    category: "العدوى البكتيرية والجرثومية",
    summary: "دليل التعامل السريع مع القوباء البكتيرية السطحية المصحوبة بقشور ذهبية عسلية، وسبل مكافحة العدوى ومنع انتشارها بين العائلة والأطفال.",
    fullInformation: {
      whatIsIt: "القوباء (Impetigo) هي عدوى بكتيرية حادة وسطحية للجلد، تُعتبر شديدة العدوى ومباشرة الانتقال، وتصيب غالبًا طبقات البشارة العلوية لدى الأطفال والبالغين على حد سواء.",
      causes: "الإصابة المباشرة بالبكتيريا العنقودية الذهبية (Staphylococcus aureus) أو البكتيريا العقدية المقيحة (Streptococcus pyogenes)، سواء عبر الجلد السليم (أولية) أو من خلال تشقق جلدي ناتج عن إكزيما، لدغة حشرة، أو خدش بسيط (ثانوية).",
      symptoms: "ظهور فقاعات حمراء دقيقة لا تلبث أن تنفجر تاركة إفرازات تتصلب مشكلة قشوراً مميزة جداً بلون عسلي أو ذهبي (Honey-colored crusts)، وتتركز خاصة حول فتحتي الأنف والحم والفم، أو في الأيدي والرقبة، مصحوبة بحكة ورغبة بالفرك.",
      treatment: "المضادات الحيوية الموضعية الفعالة مثل دهن (Mupirocin - موبيروسين) أو (Fusidic acid - فيوسيديك أسيد) تُدهن على المناطق المصابة لعدة أيام؛ وفي حالات الطفح واسع الانتشار أو المقاوم يتم اللجوء للمضادات الحيوية الفمويّة (مثل سيفالكسين أو أموكسيسيلين/كلافولانيك).",
      selfCare: "غسل الأيدي بالماء والصابون المطهر وتجنب لمس القشور أو إزالتها بعنف، فصل المناشف والشراشف ومستلزمات العناية الخاصة بالمصاب وغسلها بحرارة عالية، وعزل الطفل عن الرياضات المدرسية والاحتكاك المباشر بالآخرين حتى انقضاء 48 ساعة من بدء المضاد الحيوي الفعّال."
    },
    images: [
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "perioral-dermatitis",
    title: "التهاب الجلد حول الفم والعينين (Perioral & Periorbital Dermatitis)",
    enTitle: "Perioral Dermatitis & Facial Inflammatory Eruption",
    category: "التهابات الجلد والوجه",
    summary: "نشرة طبية للتخلص من الطفح الوردي الحارق وحبوب الوجه الدقيقة المحيطة بالفم، وكيفية الفطام العلاجي عن الستيرويدات الموضعية المسببة.",
    fullInformation: {
      whatIsIt: "التهاب الجلد حول الفم هو اضطراب التهابي جلدي شائع وحاد بصرياً، يجمع بين سمات الأكزيما وحب الشباب والوردية، ويمتلك ميزة سريرية فريدة بتفادي منطقة الحلقة الجلدية الضيقة الملاصقة مباشرة للشفاه (Sparing of the vermilion border).",
      causes: "السبب الشائع والمحفز الرئيسي هو الاستخدام المفرط وغير الموجه لكريمات ومراهم الكورتيزون (الستيرويدات الموضعية) على الوجه، أو بخاخات الصدر المحتوية على كورتيزون، إلى جانب معاجين الأسنان بالفلورايد، ومستحضرات التجميل الثقيلة الانسدادية.",
      symptoms: "بثور دقيقة ذات لون أحمر إلى وردي مجتمعة في بقع التهابية حول الفم، وأحياناً تمتلك تقاطر باتجاه زوايا الأنف (Perinasal) أو حول محيط العينين (Periorbital)، مصحوبة بشعور مزعج بالشد والجفاف والحرقة أو اللسع أكثر من الحكة.",
      treatment: "الإيقاف الحازم والتدرجي لمراهم الكورتيزون الموضعية (أمرٌ قد يسبّب تهيجاً مؤقتاً بالهجمة الانتدابية)، والبدء ببدائل علاجية متخصصة مثل كريم الميترونيدازول الموضعي (Metronidazole)، حمض الأزلائيك، أو مضادات التتراسيكلين الفموية (الدغمية مثل Doxycycline) لفترة امتدادية من 6 إلى 8 أسابيع.",
      selfCare: "التوقف التام عن تطبيق مرطبات التجميل الثقيلة والمكياج وأقنعة الوجه التزيينية خلال العلاج، الاعتماد حصراً على الغسولات المائية الملطفة الخالية من الصابون والروائح الطيارّة، واستبدال معجون الأسنان بنوع لطيف خالي من النكهات القوية."
    },
    images: [
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "acanthosis-nigricans",
    title: "الشواك الأسود وتصبغات الثنيات (Acanthosis Nigricans)",
    enTitle: "Acanthosis Nigricans & Metabolic Pigmentation",
    category: "اضطرابات التصبغ والمقاومة الأيضية",
    summary: "مرجع شامل لتفسير اسمرار وزيادة سمك ثنيات الجلد المخملية في الرقبة والإبطين، والربط التشخيصي والعلاجي الهام مع مقاومة الأنسولين.",
    fullInformation: {
      whatIsIt: "الشواك الأسود هو حالة جلدية تشخيصية متميزة بظهور بقع سميكة داكنة وسوداء مائلة إلى اللون الرمادي أو البني، تكتسب ملمساً قطيفياً مخملاُ خشناً قليلاً على الجلد في مناطق الطيات والثنايا الطبيعية للجسم.",
      causes: "ترتبط الحالة وثيقاً بمقاومة الأنسولين (Insulin Resistance) والزيادة المفرطة في مستويات الأنسولين بالدم لدى مرضى السكري أو من يعانون من السمنة المتفوقة، مما يُشعر مستقبلات عوامل النمو في الخلايا الكيراتينية وتفرز طبقات جلدية زائدة وداكنة؛ وقد ينتج أيضاً عن متلازمة تكيس المبايض (PCOS) أو تأثير بعض الأدوية الهرمونية والمكملات.",
      symptoms: "تغير تدريجي وبطيء نحو الاسمرار وزيادة سماكة وخشونة الجلد في طيات وثنيات الرقبة (المنطقة الخلفية والجوانب)، والإبطين، المغبن وأحيانًا ثنيات المفاصل واليدين؛ بدون وجع أو ألم ولكن قد يتصاحب في بعض الأحيان مع رائحة طفيفة بسبب انطواء الجلد والتعرق.",
      treatment: "العلاج الأساسي والمركزي ليس تجميلاً بحد ذاته، بل علاج العامل الميكروبي أو الهرموني المسبب: خفض الوزن الفعّال، واستخدام المنظمات الغذائية والأيضية (مثل الميتفورمين Metformin) للحد من مقاومة الأنسولين؛ أما للجانب التجميلي فتُساعد كريمات التفتيح وتقشير الرتينوئيد الموضعي (Retinoids) وأحماض الألفا هيدروكسي (AHA) على تنعيم البشارة.",
      selfCare: "تنبيه هام للمصابين بضرورة عدم الحكّ والفرك الخشن باستخدام والليف الخشن والفرك المبالغ فيه (لأن الفرك الزائد يزيد من فرط التقرن والتحزز الاسودادي)، والالتزام الرياضي المنتظم بنظام النضرة من منخفض الكربوهيدرات المكررة."
    },
    images: [
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "erythema-nodosum",
    title: "الحمامى العقدية والتهاب الدهن الحوطي (Erythema Nodosum)",
    enTitle: "Erythema Nodosum & Panniculitis",
    category: "التهابات الطبقات الدهنية",
    summary: "نشرة تعريفية عن العقيدات والبقع الحمر الساخنة والمؤلمة التي تظهر على الساقين، وأبرز المسببات المناعية والميكروبية وكيفية تسكين الخناصة التفاعلية.",
    fullInformation: {
      whatIsIt: "الحمامى العقدية هي الشكل الأكثر شيوعاً لالتهاب الطبقة الدهنية تحت الجلد (Panniculitis - التهاب السبلة الشحمية)، وتُمثل استجابة فرط تحسسي مناعي تسبب ظهور كتل وعقيدات التهابية حادة ومؤلمة.",
      causes: "غالباً ما تكون رد فعل مناعي وتفاعلي مسببات أخرى: كعدوى الجهاز التنفسي العلوي بكتيرية (مثل البكتيريا العقدية Streptococcus)، مرض الساركويد، التهابات الأمعاء التجميلية المناعية (مرض كرون والتهاب القولون)، أو نتيجة تفاعل دوائي تحسسي تجاه بعض المضادات الحيوية وحبوب منع الحمل الهرمونية.",
      symptoms: "عقيدات تورمية مستديرة أو بيضاوية ذات لون أحمر زاهٍ أو قرمزي ساخن شديدة الألم عند اللمس؛ تظهر نمطياً وبشكل أساسي على الساقين (أمامي وجانبي عظمة القصبة Shin)، وتتحول تدريجياً خلال أيام وأسابيع لتبدو مثل لون الكدمات الزرقاء ثم الصفراء قبل أن تتلاشى بدون إحداث ندبات، وغالباً ما تترافق مع إرهاق عام وحمى خفيفة وآلام بالمفاصل.",
      treatment: "العلاج يبدأ أولاً بتشخيص ومعالجة العامل الأساسي المثير للمنظومة المناعية، ثم الاعتماد العلاجي على مضادات الالتهاب غير الستيرويدية (NSAIDs مثل الإيبوبروفين أو الإندوميتاسين) لتسكين الألم وتقليل الالتهاب، وفي الحالات المقاومة والشديدة يصف الطبيب الستيرويدات الفموية بجرعات تتناقص تدريجياً أو مركب يوديد البوتاسيوم الطبية.",
      selfCare: "الراحة الجسدية الكاملة والالتزام برفع الساقين إلى مستوي أعلى من القلب أثناء الجلوس أو النوم لتجنب الركود الوريدي وزيادة الاحمرار، ووضع كمادات باردة ملطفة فوق العقيدات المتورمة لتخفيف حدة الخفقتان، مع تجنب الأنشطة الرياضية المرهقة والوقوف المتأخر حتى تنحسر النوبات التي تستمر عادة لـ 3-6 أسابيع."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "prurigo-nodularis",
    title: "الحكة العقدية والمناطق المتحززة (Prurigo Nodularis)",
    enTitle: "Prurigo Nodularis & Chronic Neuroimmune Pruritus",
    category: "الاضطرابات الحكية والمناعية",
    summary: "دليل إدارة وعلاج العقد السطحية الخشنة والمؤلمة الناتجة عن دورة الحكة والفرك العصبية المستمرة، وأجدد خيارات المنظمات المناعية والأشعة الضوئية.",
    fullInformation: {
      whatIsIt: "الحكة العقدية (Prurigo Nodularis) هي مرض جلدي عصبي ومناعي مزمن ومعقد، يتمثل بتشكل عقد صلبة ومثخنة ومتقرنة شديدة التورم والإبهار، وتُعدّ مصاحبة لأحد أعلى وأشد درجات الحكة الجلدية في الطب السريري (Intense Unrelenting Pruritus).",
      causes: "نلخص السبب في تفعيل دورة (الحكة - الهرش - الحكة المستمرة)، حيث تؤدي المحفزات العصبية أو التحسسية الجلدية (مثل الإكزيما السابقة، أمراض الكلى الكهفية، الاضطرابات التوترية الهرمونية، أو سوء تنظيم المستقبلات العصبية في الأدمة) إلى حك متواصل يُنتج فرطاً في تبلور الأعصاب الحكية داخل النتوء العقدي نفسه.",
      symptoms: "ظهور عشرات أو مئات من العقيدات المرتفعة الجافة والمقنطرة والمقاومة للملمس، بحجم حبة الحمص أو الأكبر، تنتشر على المناطق المتاحة للوصول باليد مثل الذراعين الخارجية، الظهر، الكتفين، والساقين الكرتين؛ مع حكة جنونية أو لسعات تجبر المريض على الهرش حتى إدماء العقيدة وتكرر التقشير.",
      treatment: "كسر الحلقة المفرغة يتطلب خطة علاجية مدمجة: ستيرويدات موضعية فائقة القوة تحت ضمادات مغلقة انسدادية (Occlusive Dressing)، حقن الستيرويد داخل العقد نفسها، كريمات التخدير الموضعي أو مركب الكابسيسين لتقليل الإثارة العصبية الحكية، العلاج بالأشعة فوق البنفسجية (UVB Phototherapy)، وفي الحالات المتطورة يتم الآن اعتماد العلاج البيولوجي الموجه الحديث (مثل Dupilumab).",
      selfCare: "تقليم الأظافر وبردها بشكل فائق القصر لتجنب تمزق النسيج وعقد النزف أثناء النوم الحكي غير الإرادي، ارتداء قفازات قطنية ناعمة ليلاً، الاعتماد على ترطيب البشارة المكثف والكمادات المثلجة بدلاً من الهرش عند انطلاق النوبة العصبية للحك."
    },
    images: [
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "erythema-multiforme",
    title: "الحمامى متعددة الأشكال والطفح المستهدف (Erythema Multiforme)",
    enTitle: "Erythema Multiforme (Targetoid Lesions)",
    category: "التفاعلات التحسسية والمناعية الحادة",
    summary: "مرجع سريري للتعامل مع الطفح الجلدي متعدد الوجوه والآفات المشابهة لأهداف الرماية (Target Lesions) كاستجابة للمحفزات الوبائية والدوائية.",
    fullInformation: {
      whatIsIt: "الحمامى متعددة الأشكال هي تفاعل حاد وفرط تحسسي للبشرة والأغشية المخاطية، تمتاز بظهور طفح جلدي فريد في هيئته يشبه شارة أو نقطة التصويب في ألعاب الرماية (Target-like lesions)، وتنقسم سريرياً إلى هيئة خفيفة (Minor) تشمل الجلد أساساً وهيئة شديدة أو واسعة (Major) تمتد للقرنية والشفاه والأغشية المخاطية.",
      causes: "أغلب الحالات (حوالي 90%) تنشأ كرد فعل تحسسي وتفاعلي لنوبة تسببت بها فيروسات أو عدوى ميكروبية؛ وفي مقدمتها فيروس الهربس البسيط (Herpes Simplex Virus - HSV-1 و HSV-2)، أو عدوى الميكروب الرئوي (Mycoplasma pneumoniae)، وفي نسبة أقل تكون تفاعلاً مناعياً تجاه أدوية مثل السلفا أو مضادات التشنج.",
      symptoms: "تطوّر آفات جلدية مستديرة ذات ثلاث طيات لونية محيطية متمايزة بشكل دائرة استهداف: مركز حالم أو فقاعي متقرح، تليها حلقة باهتة متورمة، وحوط خارجي أحمر داكن ملتهب؛ تنتشر وبشكل متناظر على راحة اليدين، باطن القدمين، ومحيط السواعد والأطراف، مترافقة مع تقرحات مؤلمة في الشفاه واللثة عند بعض المرضى.",
      treatment: "الحمامى الخفيفة تزول تلقائياً وغالباً ما تتطلب علاجاً داعماً فقط عبر الستيرويدات الموضعية ومضادات الهيستامين لتسكين الحكة؛ أما الحالات المصحوبة بالهربس المتكرر فيتم منح المرضى علاجاً وقائياً من المضادات للفيروسات (Acyclovir أو Valacyclovir)، وفي الحالات التقرحية الشديدة بالفم يُعطى المصاب غسولات موضعية مسكنة وأحياناً الكورتيزون الفموي القصير.",
      selfCare: "حماية الآفات المركزية المتقرحة من العدوى عبر الغسيل بلطف بماء وملح أو مطهر خالي من الكحول، شرب السوائل الدافئة والباردة وتناول المغذيات اللينة الباردة في حال وجود تقرحات بالفم والبلعوم، والتأكد الفوري لدى الطبيب من التوقف عن أي عقار جديد تزامن بَدؤُه مع بداية تشكل هذه الدوائر المستهدفة."
    },
    images: [
      "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "granuloma-annulare",
    title: "الورم الحبيبي الحلقي والندب الدائرية (Granuloma Annulare)",
    enTitle: "Granuloma Annulare",
    category: "الأمراض الحبيبية والمزمنة",
    summary: "نشرة تعريفية عن الطفح الجلدي الحميد المتمثل بكتل دقيقة تصطف في دوائر متسعة، وحقيقة زوالها الذاتي وعلاجات الإذابة الموضعية.",
    fullInformation: {
      whatIsIt: "الورم الحبيبي الحلقي هو طفح جلدي حميد ومزمن (رغم تسميته بالورم إلا أنه ليس سرطاناً ولا يمت بصلة للأورام الخبيثة بتاتاً)؛ حيث يتشكل من تجمع لخلايا مناعية وحبيبية تصطف في تكوين هلالي أو دائري متصل حول نسيج جلدي طبيعي أو مائل لليونة.",
      causes: "السبب الرئيسي الدقيق لا يزال غير محدد بالكامل سريرياً، ويعتقد أنه رد فعل مناعي متقن وتحسس نسيجي بطيئ لتدهور محدود في الكولاجين؛ وقد يتم تحفيزه في بعض الأنماط بواسطة لدغات الحشرات، الإصابات الطفيفة للجلد، أو يتصاحب بشكل طفيف مع اضطرابات الغدة الدرقية ومرض السكري في النوع المتنامي الواسع (Generalized).",
      symptoms: "ظهور حلقات أو دوائر مكونة من حبيبات وعقيدات صغيرة صلبة ومتراصة، يتدرج لونها بين لون الجلد الفعلي، الوردي، أو البنفسجي الباهت؛ وغالباً ما تشاهد بوضوح على أطراف أصابع اليدين، الظهر، الكاحلين، والقدمين من الأعلى، ولا تكون مصحوبة بألم وتقع أحياناً حكة ضيئلة طفيفة.",
      treatment: "نظراً لأن الطفح حميد وغالباً ما يتلاشى تلقائياً دون تدخل خلال شهور أو بضع سنوات بدون إحداث ندوب دائمة، فإن عدم التدخل والمراقبة يعتبر خياراً سليماً؛ وللراغبين بالعلاج السريع لأسباب تجميلية يتم استخدام الكريمات الستيرويدية فائقة القوة تحت ضمادات مغلقة، أو الحقن السريري للستيرويد الموضعي المتبخر داخل حدود الحلقة مباشرة لترجيعه.",
      selfCare: "الطمأنينة الكاملة واليقظة بأن التغيرات الجلدية ليست فطريات معدية (فكثيراً ما يُخطئ المرضى بخلطه مع التينيا الحلقية المعدية Ringworm دون استجابة لكريمات الفطريات)، وتجنب تطبيق المواد العطرية المقشرة الحارقة التي قد تتسبب في تحريض إضافي للخلايا الحبيبية."
    },
    images: [
      "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80"
    ]
  },
  {
    id: "dyshidrotic-eczema",
    title: "أكزيما خلل التعرق وفقاعات اليدين والقدمين (Dyshidrotic Eczema & Pompholyx)",
    enTitle: "Dyshidrotic Eczema / Pompholyx",
    category: "أكزيما الأطراف والتحسس",
    summary: "دليل إرشادي للتنقل الآمن خلال نوبات الأكزيما الفقاعية الدقيقة والساخنة في الأصابع والراحة، وحلول التجفيف وحفظ الترطيب العميق للمناطق المتأثرة.",
    fullInformation: {
      whatIsIt: "أكزيما خلل التعرق (المعروفة سريرياً بالـ Pompholyx أو الإكزيما الفقاعية اليدوية) هي نمط فرعي وخاص من الإكزيما يتميز بإنتاج اندماجي لفقاعات مائية شفافة وشديدة الصغر تشبه حبات الكافيار، تدمر هدوء باطن اليدين والجانبين الخلويين للأصابع.",
      causes: "على خلاف التسمية القديمة (خلل التعرق)، فإن الغدد العرقية لا تعتبر مكسورة أو تالفة، إنما ينشأ هذا التهيّج نتيجة التوتر العصبي والنفسي المرتفع، التعرق اليدوي المفرط في فصلي الربيع والصيف، التحسس بالتلامس لمعادن معينة مثل النيكل والكروم والكوبالت، أو الارتباط بتاريخ عائلي للأكزيما التأتبيّة.",
      symptoms: "ظهور فقاعات مائية صغيرة جداً، عميقة التموضع وشبّه صلبة تحت سطح الجلد في جَوانب وأصابع اليدين أو بطن القدم، تصاحبها حكة عاصفة وحرقة شديدة ونبض موضعي، وعند جفاف الفقاعات تتجعد البشارة وتتحول لطبقة صلبة حمراء ومتقشرة ومؤلمة قابلة للتشقق والنزف.",
      treatment: "الكورسات السريعة من الستيرويدات الموضعية عالية الفعالية (مثل مرهم Clobetasol أو Betamethasone) تُطبق في بداية النوبة؛ ويُصاحبها نقع اليدين بمحاليل مجففة ومطهرة مثل محلول برمنغنات البوتاسيوم المخفف أو كمادات حمض البوريك لتجفيف الفقاعات الناعمة، ومع استخدام كريمات مثبطات الكالسينيورين (Tacrolimus) لتجنب الإدمان الستيرويدي.",
      selfCare: "الامتناع القطعي والمنظم عن فقع أو إبر البثور والفقاعات لتلافي انتشار العدوى البكتيرية الثانوية، استبدال الصابون العادي ببدائل طبية دهنية بدون رغوة وتنشيف اليدين بالربت اللطيف، لبس قفازات قطنية 100% داخل القفازات المطاطية والمنزلية لمنع احتقان الرطوبة والتعرق المباشر."
    },
    images: [
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=80",
      "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=600&q=80"
    ]
  }
];

export default function PatientLeafletsViewer({ onBack, isAdmin = false }: { onBack: () => void; isAdmin?: boolean }) {
  const [leaflets, setLeaflets] = useState<Leaflet[]>(DEFAULT_LEAFLETS);
  const [selectedLeaflet, setSelectedLeaflet] = useState<Leaflet | null>(null);
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const [uploadingForId, setUploadingForId] = useState<string | null>(null);
  const [expandedGalleries, setExpandedGalleries] = useState<Record<string, boolean>>({});
  const [isSavingCloud, setIsSavingCloud] = useState(false);
  const [cloudSaveMessage, setCloudSaveMessage] = useState<string | null>(null);

  const toggleGallery = (leafletId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedGalleries((prev) => ({ ...prev, [leafletId]: !prev[leafletId] }));
  };

  // Load saved custom photos from localStorage and sync immediately with Cloud Backend
  useEffect(() => {
    const saved = localStorage.getItem("iq_doctor_bad_leaflets");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const parsedMap = new Map(parsed.map((l: any) => [l.id, l]));
          const merged = DEFAULT_LEAFLETS.map((defaultItem) => {
            const savedItem = parsedMap.get(defaultItem.id);
            return savedItem ? { ...defaultItem, images: savedItem.images } : defaultItem;
          });
          setLeaflets(merged);
        }
      } catch (e) {
        console.error("Failed loading leaflets", e);
      }
    }

    // Always fetch live synced version from Cloud Run server to override mobile browser cache
    fetch("/api/clinic/leaflets", { cache: 'no-store' })
      .then(res => res.json())
      .then((cloudLeaflets) => {
        if (Array.isArray(cloudLeaflets)) {
          const parsedMap = new Map(cloudLeaflets.map((l: any) => [l.id, l]));
          const merged = DEFAULT_LEAFLETS.map((defaultItem) => {
            const cloudItem = parsedMap.get(defaultItem.id);
            return cloudItem ? { ...defaultItem, images: cloudItem.images } : defaultItem;
          });
          setLeaflets(merged);
          localStorage.setItem("iq_doctor_bad_leaflets", JSON.stringify(merged));
        }
      })
      .catch(e => console.error("Could not sync cloud leaflets:", e));
  }, []);

  const saveToStorage = (updated: Leaflet[]) => {
    setLeaflets(updated);
    try {
      localStorage.setItem("iq_doctor_bad_leaflets", JSON.stringify(updated));
    } catch (e) {
      console.error("Error saving leaflets to localStorage", e);
    }
  };

  const handleConfirmSaveCloud = async () => {
    setIsSavingCloud(true);
    setCloudSaveMessage(null);
    saveToStorage(leaflets);
    try {
      const token = localStorage.getItem("clinic_token") || localStorage.getItem("token") || localStorage.getItem("iq_doctor_jwt") || "";
      const res = await fetch("/api/clinic/leaflets", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ leaflets })
      });
      if (res.ok) {
        setCloudSaveMessage("✅ تم تأكيد الحفظ ومزامنة كافة الصور والتعديلات عبر السحابة لجميع أجهزة الموبايل والتابلت!");
      } else {
        setCloudSaveMessage("⚠️ تم حفظها محلياً، تعذر المزامنة السحابية اللحظية.");
      }
    } catch (e) {
      console.error("Cloud saving error", e);
      setCloudSaveMessage("✅ تم الحفظ المحلي بنجاح (وضع الاتصال اللاسلكي).");
    } finally {
      setIsSavingCloud(false);
      setTimeout(() => setCloudSaveMessage(null), 6000);
    }
  };

  const handleAddPhotos = (leafletId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingForId(leafletId);
    
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          const newImage = event.target.result as string;
          setLeaflets((prev) => {
            const updated = prev.map((item) => {
              if (item.id === leafletId) {
                return { ...item, images: [newImage, ...item.images] };
              }
              return item;
            });
            saveToStorage(updated);
            if (selectedLeaflet && selectedLeaflet.id === leafletId) {
              setSelectedLeaflet((current) => current ? { ...current, images: [newImage, ...current.images] } : null);
            }
            return updated;
          });
        }
      };
      reader.readAsDataURL(file);
    });
    setUploadingForId(null);
  };

  const handleDeletePhoto = (leafletId: string, indexToDelete: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setLeaflets((prev) => {
      const updated = prev.map((item) => {
        if (item.id === leafletId) {
          return {
            ...item,
            images: item.images.filter((_, idx) => idx !== indexToDelete)
          };
        }
        return item;
      });
      saveToStorage(updated);
      if (selectedLeaflet && selectedLeaflet.id === leafletId) {
        setSelectedLeaflet((current) => current ? { ...current, images: current.images.filter((_, idx) => idx !== indexToDelete) } : null);
      }
      return updated;
    });
  };

  const handleShare = (leaflet: Leaflet, method: "whatsapp" | "general") => {
    const text = `*التثقيف الصحي - نشرات معلومات المريض من مرجع BAD*\n\n*${leaflet.title}*\n\n*ما هو المرض؟*\n${leaflet.fullInformation.whatIsIt}\n\n*الأعراض:* ${leaflet.fullInformation.symptoms}\n\n*العلاج والإرشادات:* ${leaflet.fullInformation.treatment}\n\n📍 *المصدر:* موقع الدكتور واثق الجلبي | IQ-Doctor (بالتعاون مع الجمعية البريطانية لطب الجلد BAD)`;

    if (method === "whatsapp") {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    } else {
      if (navigator.share) {
        navigator.share({
          title: leaflet.title,
          text: text,
          url: window.location.href,
        }).catch(() => {});
      } else {
        navigator.clipboard.writeText(text);
        setShareFeedback("تم نسخ النشرة الطبية للرابط!");
        setTimeout(() => setShareFeedback(null), 3000);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pt-16 md:pt-24 pb-12 px-4 relative overflow-hidden font-sans" dir="rtl">
      {/* Background decor */}
      <div className="absolute top-0 left-0 w-full h-[45vh] md:h-96 bg-gradient-to-b from-slate-900 via-slate-900 to-emerald-950 rounded-b-[4rem] z-0 shadow-2xl" />

      <div className="max-w-6xl mx-auto w-full relative z-10 flex-1 flex flex-col">
        {/* Back navigation */}
        <button 
          onClick={onBack}
          className="self-end mb-8 bg-white/10 hover:bg-white/20 text-white rounded-full py-2 px-5 flex items-center gap-2 transition-all border border-white/20 backdrop-blur-md shadow-lg cursor-pointer"
        >
          <ChevronRight className="w-4 h-4" />
          <span className="text-sm font-bold">العودة للبوابة الطبية</span>
        </button>

        {/* Header Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-emerald-500/20 rounded-full mb-4 border border-emerald-400/30 backdrop-blur-sm shadow-xl">
            <FileText className="w-10 h-10 text-emerald-400 animate-pulse" />
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight drop-shadow-md">
            التثقيف الصحي - نشرات معلومات المريض
          </h1>
          <p className="text-emerald-100 max-w-3xl mx-auto text-sm md:text-lg leading-relaxed font-semibold">
            نشرات تعليمية وتثقيفية باللغة العربية مقتبسة ومترجمة بعناية من موقع الجمعية البريطانية لطب الأمراض الجلدية (<span className="font-mono font-black text-amber-300">BAD</span>) مدعومة بالصور الموثقة والقابلة لمشاركة المعلومات السريرية.
          </p>
          <div className="my-4 flex items-center justify-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/30 via-slate-800/60 to-amber-500/30 text-white font-black text-xs md:text-sm px-5 py-2 rounded-full border border-emerald-400/50 shadow-xl backdrop-blur-md">
              <span>✨ المجموعة الـ 3 متوفرة الآن: موسوعة شاملة تضم 30 مرجعاً سريرياً وحالة جلديّة موثقة من (BAD)</span>
            </div>
          </div>
          {isAdmin ? (
            <div className="mt-4 flex flex-col items-center gap-3">
              <div className="inline-flex items-center gap-2 bg-amber-500/20 px-4 py-1.5 rounded-full border border-amber-400/30 text-xs text-amber-200 font-bold">
                <span>👑 وضع إدارة السوبر أدمن: يمكنك الآن رفع وإضافة أكثر من 3 صور لكل مرض أو حذفها بكل حرية</span>
              </div>
              <button
                onClick={handleConfirmSaveCloud}
                disabled={isSavingCloud}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white font-extrabold text-sm rounded-full shadow-xl border border-emerald-300/40 transform hover:-translate-y-0.5 active:translate-y-0 transition duration-300 select-none disabled:opacity-50"
              >
                {isSavingCloud ? (
                  <>
                    <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    <span>جاري تأكيد الحفظ والمزامنة في السحابة...</span>
                  </>
                ) : (
                  <>
                    <span className="text-base">💾</span>
                    <span>تأكيد وحفظ تعديلات الصور في السحاب (لتعميمها بالهاتف والتابلت)</span>
                  </>
                )}
              </button>
              {cloudSaveMessage && (
                <div className="bg-emerald-600 text-white text-xs md:text-sm font-bold px-4 py-2 rounded-xl shadow-lg border border-emerald-300 animate-in fade-in zoom-in duration-300">
                  {cloudSaveMessage}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4 inline-flex items-center gap-2 bg-white/10 px-4 py-1.5 rounded-full border border-white/15 text-xs text-white font-bold">
              <span>📖 اضغط على أي صورة لعرض كامل التفاصيل والإرشادات الطبية للمريض مع إمكانية المشاركة الفورية</span>
            </div>
          )}
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {leaflets.map((item) => (
            <motion.div
              key={item.id}
              className="bg-white rounded-3xl overflow-hidden shadow-xl border border-slate-200 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1.5"
            >
              <div>
                {/* Images Showcase Header - Clickable to open info as requested */}
                <div className="relative bg-slate-900 p-2.5 border-b border-slate-100">
                  <div className="flex items-center justify-between px-2 mb-2">
                    <span className="text-[11px] font-black bg-emerald-100 text-emerald-950 px-2.5 py-0.5 rounded-full border border-emerald-300">
                      {item.category}
                    </span>
                    <span className="text-[10px] font-bold text-emerald-300 flex items-center gap-1">
                      <ImageIcon className="w-3.5 h-3.5" />
                      {item.images.length} صورة (اضغط للعرض)
                    </span>
                  </div>

                  {/* Horizontal Scrollable Images Gallery */}
                  <div 
                    onClick={() => setSelectedLeaflet(item)}
                    className="flex items-center gap-2 overflow-x-auto pb-2 px-1 scrollbar-thin cursor-pointer select-none group/gallery"
                    title="اضغط على أي صورة لفتح كامل المعلومات السريرية والمشاركة"
                  >
                    {(expandedGalleries[item.id] ? item.images : item.images.slice(0, 3)).map((imgUrl, idx) => (
                      <div key={idx} className="relative shrink-0 w-36 h-32 rounded-2xl overflow-hidden border-2 border-slate-700 hover:border-emerald-400 transition-all shadow-md">
                        <img src={imgUrl} alt={`صورة ${item.title}`} className="w-full h-full object-cover group-hover/gallery:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-end justify-between p-1.5">
                          <span className="text-[9px] text-white font-bold">عرض التفاصيل</span>
                          {isAdmin && item.images.length > 1 && (
                            <button
                              onClick={(e) => handleDeletePhoto(item.id, idx, e)}
                              title="حذف الصورة"
                              className="bg-red-600 hover:bg-red-700 text-white p-1 rounded-md transition"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    {item.images.length > 3 && (
                      <button
                        onClick={(e) => toggleGallery(item.id, e)}
                        className="shrink-0 w-16 h-32 bg-slate-800/95 hover:bg-emerald-600 border-2 border-slate-700 hover:border-emerald-400 rounded-2xl flex flex-col items-center justify-center gap-1.5 text-white transition-all duration-300 shadow-md group/btn"
                        title={expandedGalleries[item.id] ? "إخفاء الصور الإضافية" : "عرض المزيد من الصور"}
                      >
                        {expandedGalleries[item.id] ? (
                          <>
                            <ChevronRight className="w-6 h-6 animate-pulse text-emerald-400 group-hover/btn:text-white" />
                            <span className="text-[10px] font-black">إخفاء</span>
                          </>
                        ) : (
                          <>
                            <ChevronRight className="w-6 h-6 rotate-180 animate-pulse text-emerald-400 group-hover/btn:text-white" />
                            <span className="text-[10px] font-black">+{item.images.length - 3} وأكثر</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Add Image Button (Only visible for Super Admin) */}
                  {isAdmin && (
                    <div className="px-1 pt-1 flex justify-between items-center">
                      <label className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 hover:text-amber-200 text-xs font-bold py-2 px-3 rounded-xl border border-amber-500/30 flex items-center justify-center gap-2 cursor-pointer transition shadow-sm">
                        <Plus className="w-4 h-4 text-amber-400" />
                        <span>{uploadingForId === item.id ? "جاري الإضافة..." : "إضافة صور أخرى للمرض (+3 أقصى حر)"}</span>
                        <input 
                          type="file" 
                          multiple 
                          accept="image/*" 
                          className="hidden" 
                          onChange={(e) => handleAddPhotos(item.id, e.target.files)} 
                          disabled={uploadingForId === item.id}
                        />
                      </label>
                    </div>
                  )}
                </div>

                {/* Content Body */}
                <div className="p-6 cursor-pointer" onClick={() => setSelectedLeaflet(item)}>
                  <div className="text-slate-400 font-mono text-xs font-bold mb-1 block" dir="ltr">
                    {item.enTitle}
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900 mb-3 group-hover:text-emerald-700 transition-colors leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-xs leading-relaxed line-clamp-3 font-semibold mb-4">
                    {item.summary}
                  </p>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2 mt-auto">
                <button
                  onClick={() => setSelectedLeaflet(item)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs py-2.5 px-4 rounded-xl transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span>تفتح المعلومات</span>
                </button>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleShare(item, "whatsapp")}
                    title="مشاركة عبر واتساب"
                    className="bg-[#25D366] hover:bg-[#128C7E] text-white p-2.5 rounded-xl transition shadow-sm cursor-pointer"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleShare(item, "general")}
                    title="مشاركة المعلومات والنسخ"
                    className="bg-slate-200 hover:bg-slate-300 text-slate-800 p-2.5 rounded-xl transition cursor-pointer font-bold flex items-center gap-1 text-xs"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Global Footer with website name as requested */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-lg mt-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-right">
            <div>
              <h4 className="font-extrabold text-slate-900 text-base flex items-center justify-center md:justify-start gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
                التثقيف الصحي - نشرات معلومات المريض
              </h4>
              <p className="text-xs text-slate-500 font-semibold mt-1">
                اسم الموقع: <strong className="text-slate-900">موقع الدكتور واثق الجلبي | IQ-Doctor</strong> (البوابة الطبية المتقدمة).
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 px-4 py-2.5 rounded-2xl text-xs font-bold text-emerald-900 text-center">
              المراجع المعتمدة: الجمعية البريطانية لطب الأمراض الجلدية <span className="font-mono font-black text-indigo-700">BAD</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal / Popup for viewing full leaflet information when pressing picture or button */}
      <AnimatePresence>
        {selectedLeaflet && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md overflow-y-auto" onClick={() => setSelectedLeaflet(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl border border-slate-100 max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden text-right my-auto"
            >
              {/* Modal Header */}
              <div className="bg-slate-900 text-white p-6 border-b border-slate-800 flex justify-between items-start relative overflow-hidden shrink-0">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-600/10 rounded-full blur-2xl pointer-events-none" />
                <div className="pr-2 z-10">
                  <span className="text-[11px] font-mono bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full border border-emerald-500/30 font-bold inline-block mb-2">
                     مرجع BAD التثقيفي • {selectedLeaflet.enTitle}
                  </span>
                  <h2 className="text-2xl font-black text-white leading-tight">
                    {selectedLeaflet.title}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedLeaflet(null)}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white p-2 rounded-full transition cursor-pointer shrink-0 z-10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Scrollable Content */}
              <div className="p-6 overflow-y-auto space-y-6 text-slate-800 text-xs md:text-sm font-semibold leading-relaxed flex-1">
                
                {/* Images section inside modal */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-black text-slate-900 text-sm flex items-center gap-1.5">
                      <ImageIcon className="w-4 h-4 text-emerald-600" />
                      الصور الطبية الملحقة بالنشرة ({selectedLeaflet.images.length})
                    </span>
                    {isAdmin && (
                      <label className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold px-3 py-1.5 rounded-xl cursor-pointer transition flex items-center gap-1 shadow-xs">
                        <Plus className="w-3.5 h-3.5" />
                        <span>إضافة صور إضافية (أكثر من 3)</span>
                        <input 
                          type="file" 
                          multiple 
                          accept="image/*" 
                          className="hidden" 
                          onChange={(e) => handleAddPhotos(selectedLeaflet.id, e.target.files)} 
                        />
                      </label>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selectedLeaflet.images.map((imgUrl, idx) => (
                      <div key={idx} className="relative rounded-xl overflow-hidden shadow-sm border border-slate-300 h-36 group/modalimg">
                        <img src={imgUrl} alt="صورة توضيحية" className="w-full h-full object-cover" />
                        {isAdmin && selectedLeaflet.images.length > 1 && (
                          <button
                            onClick={(e) => handleDeletePhoto(selectedLeaflet.id, idx, e)}
                            className="absolute bottom-2 left-2 bg-red-600 text-white p-1 rounded shadow-md opacity-0 group-hover/modalimg:opacity-100 transition"
                            title="حذف"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Information detailed blocks */}
                <div className="space-y-4">
                  <div className="bg-emerald-50/60 border border-emerald-150 p-4 rounded-2xl">
                    <h4 className="font-extrabold text-emerald-950 text-base mb-1">💡 ما هو هذا المرض الجلدى؟ (What is it?)</h4>
                    <p className="text-slate-700 leading-relaxed font-bold">{selectedLeaflet.fullInformation.whatIsIt}</p>
                  </div>

                  <div className="bg-white border border-slate-200 p-4 rounded-2xl shadow-xs">
                    <h4 className="font-extrabold text-indigo-950 text-base mb-1">🔬 الأسباب والمسببات السريرية (Causes)</h4>
                    <p className="text-slate-700 leading-relaxed">{selectedLeaflet.fullInformation.causes}</p>
                  </div>

                  <div className="bg-white border border-slate-200 p-4 rounded-2xl shadow-xs">
                    <h4 className="font-extrabold text-rose-950 text-base mb-1">🩺 الأعراض والعلامات الرئيسية (Symptoms)</h4>
                    <p className="text-slate-700 leading-relaxed">{selectedLeaflet.fullInformation.symptoms}</p>
                  </div>

                  <div className="bg-indigo-50/50 border border-indigo-150 p-4 rounded-2xl">
                    <h4 className="font-extrabold text-indigo-950 text-base mb-1">💊 الخطة العلاجية الموصى بها (Treatment)</h4>
                    <p className="text-slate-800 leading-relaxed font-bold">{selectedLeaflet.fullInformation.treatment}</p>
                  </div>

                  <div className="bg-amber-50/60 border border-amber-200 p-4 rounded-2xl">
                    <h4 className="font-extrabold text-amber-950 text-base mb-1">🛡️ نصائح العناية الذاتية للمريض (Self-Care & Advice)</h4>
                    <p className="text-slate-800 leading-relaxed">{selectedLeaflet.fullInformation.selfCare}</p>
                  </div>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Share Feedback Toast */}
      {shareFeedback && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-2xl z-[100] font-bold text-sm flex items-center gap-2 border border-slate-700 animate-in fade-in slide-in-from-bottom-4">
          <Check className="w-5 h-5 text-emerald-400" />
          <span>{shareFeedback}</span>
        </div>
      )}
    </div>
  );
}
